import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  AspectRatio,
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  SimpleGrid,
  Icon,
  HStack,
  Badge,
  Divider,
  Flex,
  Image,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  keyframes,
} from '@chakra-ui/react';
import {
  FaArrowRight,
  FaCheckCircle,
  FaChartBar,
  FaChartLine,
  FaChevronDown,
  FaChevronLeft,
  FaClock,
  FaCommentDots,
  FaPalette,
  FaPhoneAlt,
  FaArrowUp,
  FaShieldAlt,
  FaCalendarAlt,
  FaBrain,
  FaBullhorn,
  FaCalculator,
  FaLightbulb,
  FaMapMarkerAlt,
  FaRocket,
  FaTag,
} from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';

import AnimatedBox from './AnimatedBox';
import LandingHeader from './LandingHeader';
import AgencyHeroReviewModule from './AgencyHeroReviewModule';
import AgencyFloatingProspectBar from './AgencyFloatingProspectBar';

// ---------------------------------------------------------------------------
// Unified design tokens
// ---------------------------------------------------------------------------
// Single source of truth for the agency landing palette. The `deep`,
// `bright`, and `sky` tokens replace ad-hoc blue hex values used across
// the page so the dark hero, gradient text, and accent colors are unified.
const BRAND = {
  primary: '#1D6EF5', // brand.bright
  primaryDark: '#0052CC',
  ink: '#0A1F44',
  deep: '#0A1E4A',
  bright: '#1D6EF5',
  sky: '#3BA6F5',
  heroBg: 'linear-gradient(135deg, #0a1835 0%, #0d2a5a 50%, #0f3a78 100%)',
  // Gradient applied to accent phrases in the hero and section headers so
  // the same blue glow appears everywhere a key phrase is highlighted.
  gradient: 'linear(to-r, #38BDF8, #3B82F6)',
};

const SECTION_PADDING = { base: 16, md: 24, lg: 32 };
const CONTAINER_MAX = '6xl';
const CARD_SHADOW = '0 1px 2px rgba(10,31,68,0.04), 0 8px 24px rgba(10,31,68,0.06)';
const CARD_SHADOW_HOVER = '0 4px 12px rgba(10,31,68,0.08), 0 20px 48px rgba(10,31,68,0.12)';

// Reusable accent text helper. Applies a soft sky→blue gradient fill to a
// phrase so highlighted words read as glow rather than flat color.
const GradientText = ({ children, ...props }) => (
  <Text
    as="span"
    bgGradient={BRAND.gradient}
    bgClip="text"
    sx={{ WebkitTextFillColor: 'transparent' }}
    {...props}
  >
    {children}
  </Text>
);

// ---------------------------------------------------------------------------
// Content (preserved from original)
// ---------------------------------------------------------------------------
const FOUR_PILLARS = [
  {
    icon: FaPhoneAlt,
    title: 'Win More Discovery Calls',
    description:
      "Walk into every discovery call with data your prospect has never seen. Show them where they rank in their market, who's beating them, and exactly what they need to fix. Close deals with proof, not promises.",
    accent: 'blue',
  },
  {
    icon: FaArrowUp,
    title: 'Upsell Existing Clients',
    description:
      'Every client dashboard includes a hyperlink to book an internal upsell call with your team. When clients see competitive gaps in real time, they ask you to fix them. Upsells happen organically.',
    accent: 'green',
  },
  {
    icon: FaShieldAlt,
    title: 'Reduce Churn',
    description:
      "Give clients a product they actually use daily. They can log in 24/7, check their market position, track competitors, and see exactly what you're doing for them. Clients who use the data don't leave.",
    accent: 'purple',
  },
  {
    icon: FaPalette,
    title: 'Completely Whitelabeled',
    description:
      'Your logo. Your colors. Your domain. Your clients never know we exist. It looks and feels like your proprietary competitive intelligence platform.',
    accent: 'orange',
  },
];

const HOW_IT_WORKS_STEPS = [
  {
    step: '01',
    title: 'Tell Us the Niche & Location',
    description:
      'Plumbers in Austin. Dentists in Miami. Coffee shops in Brooklyn. Give us the market and we scan every competitor automatically. Takes minutes, not days.',
  },
  {
    step: '02',
    title: 'We Build the Dashboard Instantly',
    description:
      'All publicly available data. No APIs, no Google Analytics, no plugins required. We pull reviews, traffic, SEO, ads, and competitor data into super digestible dashboards.',
  },
  {
    step: '03',
    title: 'You Close Deals & Retain Clients',
    description:
      "Use the dashboards in discovery calls to show prospects data they've never seen. Give existing clients 24/7 login access. Upsell based on real competitive gaps.",
  },
];

// Soft-data tiles for the "Where the data comes from" section. Hard data
// (reviews) gets its own hero panel above; everything below is derived /
// estimated and we label it that way so prospects know the difference.
const SOFT_DATA_SOURCES = [
  {
    icon: FaChartLine,
    accent: 'purple',
    title: 'Website Traffic & SEO Score',
    description:
      "Estimated from SpyFu and Semrush using each business's URL. Calibrated for ranking competitors and spotting outliers — not for measuring sub-1% week-over-week swings.",
    source: 'SpyFu + Semrush · domain-level estimates',
  },
  {
    icon: FaBrain,
    accent: 'orange',
    title: 'SWOT Analysis',
    description:
      "An AI summary of the real review text. 5-star reviews surface strengths and what's working; 1-3 star reviews surface weaknesses and what customers aren't loving.",
    source: 'AI summary · grounded in the scraped reviews',
  },
  {
    icon: FaTag,
    accent: 'pink',
    title: 'Pricing Perception',
    description:
      'Derived from keywords and star ratings inside the same Google review data. When customers mention price — and how they feel about it — it shows up here.',
    source: 'Keyword + rating signal · from review text',
  },
];

const AGENCY_FAQ = [
  {
    question: 'What data do you actually pull?',
    answer:
      'All publicly available data: Google review data (frequency, total, sentiment, every individual review), website traffic, SEO rankings and backlink profiles, ads that businesses are running, competitor ad spend, and keyword bidding data. We also run AI analysis on all reviews to generate strengths, weaknesses, opportunities, threats, and pricing perception rankings.',
  },
  {
    question: 'Do my clients need to connect any APIs or install anything?',
    answer:
      'No. Zero setup required from your clients. No Google Analytics access, no API connections, no plugins, no tracking codes. Everything is publicly available data. You give us a business name and location, we handle the rest. Instant setup.',
  },
  {
    question: 'What do clients actually see when they log in?',
    answer:
      "A polished, whitelabeled competitive intelligence dashboard branded as your agency's tool. They see review frequencies, total reviews, sentiment analysis, website traffic, SEO rankings, ad intelligence, AI-powered insights, pricing perception, seasonality data, goal tracking, action items, and a reading list of industry news. They can click on any competitor and see where they rank on every KPI.",
  },
  {
    question: 'How does the discovery call use case work?',
    answer:
      "Before a discovery call, you pull up the prospect's market. You can say: 'You're doing amazing at review growth and SEO, but your sentiment is a big issue' or 'You're 12th in SEO and 6th in traffic — we're going to get you more backlinks and implement a new review garnering system.' You walk in with context on exactly where they stand and what they need. It's a completely different conversation.",
  },
  {
    question: 'What exactly is whitelabeled?',
    answer:
      'Everything. Your logo, your colors, your domain. The login page, the dashboards, everything client-facing. Your clients never know we exist. It looks and feels like your proprietary competitive intelligence platform.',
  },
  {
    question: 'How does pricing work?',
    answer:
      "$500/month gets you 20 discovery call market reports — that's 20 new markets you can pull data on for prospect meetings. For existing clients you want to give ongoing access to, it's $90/client/month. They get a whitelabeled login and 24/7 access to their dashboard.",
  },
  {
    question: 'Can I demo this for a specific market before committing?',
    answer:
      'Yes. We offer a pilot so you can see the actual dashboards for a real market before you commit. No long-term contracts required.',
  },
  {
    question: 'How many competitors are tracked per market?',
    answer:
      'Up to 20 competitors per niche + location. You can see review frequencies, total reviews, sentiment, website traffic, SEO scores, and ad data for all of them. You can also compare any businesses head-to-head.',
  },
];

// ---------------------------------------------------------------------------
// Reusable UI primitives
// ---------------------------------------------------------------------------
const SectionHeader = ({ eyebrow, title, accent, subtitle, align = 'center' }) => (
  <VStack
    spacing={4}
    textAlign={align}
    align={align === 'center' ? 'center' : 'flex-start'}
    maxW={align === 'center' ? '720px' : 'none'}
    mx={align === 'center' ? 'auto' : 0}
  >
    {eyebrow && (
      <Badge
        bg="blue.50"
        color="blue.700"
        px={3}
        py={1}
        borderRadius="full"
        fontSize="xs"
        fontWeight="semibold"
        letterSpacing="wider"
        textTransform="uppercase"
        border="1px solid"
        borderColor="blue.100"
      >
        {eyebrow}
      </Badge>
    )}
    <Heading
      as="h2"
      fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
      color={BRAND.ink}
      fontWeight="extrabold"
      lineHeight={1.1}
      letterSpacing="-0.025em"
    >
      {title}
      {accent && (
        <>
          {' '}
          <GradientText>{accent}</GradientText>
        </>
      )}
    </Heading>
    {subtitle && (
      <Text
        fontSize={{ base: 'md', md: 'lg' }}
        color="#475569"
        lineHeight={1.65}
        maxW="640px"
      >
        {subtitle}
      </Text>
    )}
  </VStack>
);

const Card = ({ children, hover = true, ...props }) => (
  <Box
    bg="white"
    borderRadius="2xl"
    border="1px solid"
    borderColor="gray.200"
    boxShadow={CARD_SHADOW}
    p={{ base: 6, md: 7 }}
    h="full"
    transition="all 0.25s ease"
    _hover={
      hover
        ? {
            transform: 'translateY(-2px)',
            boxShadow: CARD_SHADOW_HOVER,
            borderColor: 'blue.200',
          }
        : undefined
    }
    _active={hover ? { transform: 'scale(0.99)' } : undefined}
    {...props}
  >
    {children}
  </Box>
);

const IconTile = ({ icon, accent = 'blue' }) => (
  <Flex
    w="48px"
    h="48px"
    borderRadius="xl"
    bg={`${accent}.50`}
    color={`${accent}.600`}
    align="center"
    justify="center"
    flexShrink={0}
  >
    <Icon as={icon} boxSize={5} />
  </Flex>
);

// ---------------------------------------------------------------------------
// Four Pillars spotlight - interactive, auto-rotating split layout
// ---------------------------------------------------------------------------
// The four pillars need to stand out from every other "icon + title + copy"
// grid on the page. This spotlight pairs a vertical selector on the left
// with a single oversized stage on the right. The stage swaps with a soft
// crossfade, a unique animated micro-visual plays per pillar, and an
// ambient color glow shifts behind the section. Auto-rotation pauses on
// hover / focus, and the bottom progress bar on the active selector row
// makes the timing visible without being noisy.
const FOUR_PILLARS_ROTATE_MS = 6500;

const PILLAR_ACCENTS = {
  blue: {
    base: '#1D6EF5',
    soft: '#E8F0FE',
    border: '#BFDBFE',
    glow: 'rgba(29,110,245,0.22)',
    gradient: 'linear-gradient(135deg, #38BDF8 0%, #2563EB 100%)',
  },
  green: {
    base: '#10B981',
    soft: '#DCFCE7',
    border: '#A7F3D0',
    glow: 'rgba(16,185,129,0.22)',
    gradient: 'linear-gradient(135deg, #34D399 0%, #059669 100%)',
  },
  purple: {
    base: '#8B5CF6',
    soft: '#EDE9FE',
    border: '#DDD6FE',
    glow: 'rgba(139,92,246,0.22)',
    gradient: 'linear-gradient(135deg, #A78BFA 0%, #6D28D9 100%)',
  },
  orange: {
    base: '#F97316',
    soft: '#FFEDD5',
    border: '#FED7AA',
    glow: 'rgba(249,115,22,0.22)',
    gradient: 'linear-gradient(135deg, #FB923C 0%, #EA580C 100%)',
  },
};

const pillarRise = keyframes`
  0%, 10% { transform: scaleY(0.15); }
  60%, 100% { transform: scaleY(1); }
`;

const pillarFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const pillarRipple = keyframes`
  0% { transform: scale(0.55); opacity: 0.6; }
  100% { transform: scale(1.6); opacity: 0; }
`;

const pillarOrbit = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pillarFill = keyframes`
  from { width: 0%; }
  to { width: 100%; }
`;

// Visual 1: animated bar chart with a highlighted rank - mirrors the
// "where you stand vs. competitors" reveal that wins discovery calls.
const DiscoveryCallVisual = ({ accent }) => {
  const bars = [
    { h: 38, rank: '#15' },
    { h: 54, rank: '#9' },
    { h: 72, rank: '#5', highlight: true },
    { h: 90, rank: '#2' },
    { h: 62, rank: '#7' },
  ];
  return (
    <Flex w="full" h="full" align="flex-end" gap={3}>
      {bars.map((b, i) => (
        <Flex
          key={b.rank}
          flex="1"
          direction="column"
          align="center"
          justify="flex-end"
          h="full"
        >
          <Box
            h={`${b.h}%`}
            w="full"
            borderRadius="lg"
            bg={b.highlight ? accent.gradient : 'rgba(255,255,255,0.5)'}
            border="1px solid"
            borderColor={b.highlight ? 'transparent' : 'rgba(255,255,255,0.65)'}
            transformOrigin="bottom"
            animation={`${pillarRise} 2.4s cubic-bezier(0.22, 1, 0.36, 1) ${i * 0.12}s both`}
            boxShadow={b.highlight ? `0 10px 28px ${accent.glow}` : 'none'}
          />
          <Text
            mt={1.5}
            fontSize="xs"
            fontFamily="mono"
            color={b.highlight ? 'white' : 'rgba(255,255,255,0.85)'}
            fontWeight={b.highlight ? 'bold' : 'normal'}
            lineHeight="1"
          >
            {b.rank}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};

// Visual 2: rising trend line with floating "+$" pills - the upsell
// opportunities clients discover inside their own dashboard.
const UpsellVisual = ({ accent }) => (
  <Box position="relative" w="full" h="full">
    <Box
      position="absolute"
      inset={0}
      bgImage="linear-gradient(135deg, transparent 0 calc(50% - 1px), rgba(255,255,255,0.55) calc(50% - 1px) calc(50% + 1px), transparent calc(50% + 1px))"
    />
    <Flex
      position="absolute"
      top="6px"
      right="6px"
      w="36px"
      h="36px"
      bg="white"
      color={accent.base}
      borderRadius="full"
      align="center"
      justify="center"
      boxShadow={`0 10px 24px ${accent.glow}`}
    >
      <Icon as={FaArrowUp} boxSize={4} />
    </Flex>
    <Flex
      position="absolute"
      bottom="14%"
      left="14%"
      bg="white"
      color={accent.base}
      px={3}
      py={1.5}
      borderRadius="full"
      fontSize="sm"
      fontWeight="bold"
      fontFamily="mono"
      boxShadow="0 10px 24px rgba(15,23,42,0.18)"
      animation={`${pillarFloat} 3.4s ease-in-out infinite`}
    >
      +$500
    </Flex>
    <Flex
      position="absolute"
      top="40%"
      left="38%"
      bg="white"
      color={accent.base}
      px={3}
      py={1.5}
      borderRadius="full"
      fontSize="sm"
      fontWeight="bold"
      fontFamily="mono"
      boxShadow="0 10px 24px rgba(15,23,42,0.18)"
      animation={`${pillarFloat} 4.2s ease-in-out 0.6s infinite`}
    >
      +$1.2k
    </Flex>
    <Flex
      position="absolute"
      top="8%"
      right="22%"
      bg="white"
      color={accent.base}
      px={3}
      py={1.5}
      borderRadius="full"
      fontSize="sm"
      fontWeight="bold"
      fontFamily="mono"
      boxShadow="0 10px 24px rgba(15,23,42,0.18)"
      animation={`${pillarFloat} 3.8s ease-in-out 1.1s infinite`}
    >
      +$2.4k MRR
    </Flex>
  </Box>
);

// Visual 3: shielded brand mark with rippling rings - clients who use
// the data don't leave.
const ChurnVisual = ({ accent }) => (
  <Flex w="full" h="full" align="center" justify="center" position="relative">
    {[0, 0.7, 1.4].map((d) => (
      <Box
        key={d}
        position="absolute"
        w="86px"
        h="86px"
        borderRadius="full"
        border="2px solid"
        borderColor="white"
        animation={`${pillarRipple} 2.4s ease-out ${d}s infinite`}
      />
    ))}
    <Flex
      w="86px"
      h="86px"
      borderRadius="2xl"
      bg="white"
      align="center"
      justify="center"
      color={accent.base}
      boxShadow={`0 18px 40px ${accent.glow}`}
      zIndex={1}
    >
      <Icon as={FaShieldAlt} boxSize={8} />
    </Flex>
    <Flex
      position="absolute"
      bottom="6%"
      bg="white"
      color={accent.base}
      px={3}
      py={1}
      borderRadius="full"
      fontSize="xs"
      fontWeight="bold"
      fontFamily="mono"
      boxShadow="0 6px 18px rgba(15,23,42,0.18)"
      zIndex={2}
      letterSpacing="wider"
    >
      24/7 LOGIN
    </Flex>
  </Flex>
);

// Visual 4: agency logo placeholder with brand-color swatches orbiting it
// - it looks and feels like the agency's own platform.
const WhitelabelVisual = () => {
  const swatches = ['#1D6EF5', '#8B5CF6', '#10B981', '#F97316'];
  return (
    <Flex w="full" h="full" align="center" justify="center" position="relative">
      <Box
        position="absolute"
        w="140px"
        h="140px"
        borderRadius="full"
        border="1px dashed"
        borderColor="rgba(255,255,255,0.75)"
        animation={`${pillarOrbit} 16s linear infinite`}
      >
        {swatches.map((c, i) => (
          <Box
            key={c}
            position="absolute"
            w="20px"
            h="20px"
            borderRadius="full"
            bg={c}
            top="50%"
            left="50%"
            mt="-10px"
            ml="-10px"
            transform={`rotate(${i * 90}deg) translateY(-70px)`}
            boxShadow="0 4px 10px rgba(15,23,42,0.22)"
          />
        ))}
      </Box>
      <Flex
        w="88px"
        h="88px"
        borderRadius="2xl"
        bg="white"
        align="center"
        justify="center"
        flexDirection="column"
        boxShadow="0 18px 40px rgba(15,23,42,0.22)"
        lineHeight="1"
      >
        <Text fontSize="9px" color="gray.400" fontWeight="semibold" letterSpacing="0.18em">
          YOUR
        </Text>
        <Text
          fontSize="lg"
          fontWeight="extrabold"
          letterSpacing="-0.02em"
          color={BRAND.ink}
          mt={1}
        >
          AGENCY
        </Text>
      </Flex>
    </Flex>
  );
};

const PILLAR_VISUALS = [
  DiscoveryCallVisual,
  UpsellVisual,
  ChurnVisual,
  WhitelabelVisual,
];

const FourPillarsSpotlight = ({ onCta }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return undefined;
    const id = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % FOUR_PILLARS.length);
    }, FOUR_PILLARS_ROTATE_MS);
    return () => window.clearInterval(id);
    // Re-running when activeIndex changes resets the interval so a manual
    // click gives the new slide the full rotation window before advancing.
  }, [isPaused, activeIndex]);

  const activePillar = FOUR_PILLARS[activeIndex];
  const activeAccent = PILLAR_ACCENTS[activePillar.accent];

  return (
    <Box
      position="relative"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
    >
      <Box
        position="absolute"
        top="-60px"
        left="-60px"
        right="-60px"
        bottom="-60px"
        bg={`radial-gradient(circle at 70% 30%, ${activeAccent.glow}, transparent 60%)`}
        filter="blur(40px)"
        transition="background 0.8s ease"
        pointerEvents="none"
        zIndex={0}
      />

      <SimpleGrid
        columns={{ base: 1, lg: 12 }}
        spacing={{ base: 6, lg: 10 }}
        position="relative"
        zIndex={1}
      >
        <Box gridColumn={{ lg: 'span 5' }}>
          <VStack align="stretch" spacing={2}>
            {FOUR_PILLARS.map((pillar, idx) => {
              const isActive = idx === activeIndex;
              const accent = PILLAR_ACCENTS[pillar.accent];
              return (
                <Box
                  key={pillar.title}
                  as="button"
                  type="button"
                  onClick={() => setActiveIndex(idx)}
                  textAlign="left"
                  position="relative"
                  bg={isActive ? 'white' : 'transparent'}
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor={isActive ? accent.border : 'transparent'}
                  boxShadow={isActive ? CARD_SHADOW : 'none'}
                  p={{ base: 4, md: 5 }}
                  transition="background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease, transform 0.25s ease"
                  _hover={
                    !isActive
                      ? {
                          bg: 'white',
                          borderColor: 'gray.200',
                          transform: 'translateX(2px)',
                        }
                      : undefined
                  }
                  _focusVisible={{
                    outline: '2px solid',
                    outlineColor: 'blue.400',
                    outlineOffset: '2px',
                  }}
                  overflow="hidden"
                  aria-pressed={isActive}
                >
                  <Box
                    position="absolute"
                    top="12%"
                    bottom="12%"
                    left={0}
                    w="4px"
                    borderRadius="full"
                    bg={accent.base}
                    opacity={isActive ? 1 : 0}
                    transform={isActive ? 'scaleY(1)' : 'scaleY(0.3)'}
                    transformOrigin="center"
                    transition="all 0.4s cubic-bezier(0.22, 1, 0.36, 1)"
                  />
                  <HStack spacing={4} align="center">
                    <Flex
                      w="44px"
                      h="44px"
                      borderRadius="xl"
                      bg={isActive ? accent.gradient : accent.soft}
                      color={isActive ? 'white' : accent.base}
                      align="center"
                      justify="center"
                      flexShrink={0}
                      boxShadow={isActive ? `0 8px 20px ${accent.glow}` : 'none'}
                      transition="all 0.3s ease"
                    >
                      <Icon as={pillar.icon} boxSize={5} />
                    </Flex>
                    <Heading
                      as="h3"
                      flex="1"
                      fontSize={{ base: 'md', md: 'lg' }}
                      color={BRAND.ink}
                      fontWeight="bold"
                      letterSpacing="-0.01em"
                      lineHeight={1.3}
                      m={0}
                    >
                      {pillar.title}
                    </Heading>
                    <Badge
                      flexShrink={0}
                      bg={isActive ? accent.soft : 'transparent'}
                      color={accent.base}
                      fontFamily="mono"
                      fontSize="xs"
                      px={2}
                      py={0.5}
                      borderRadius="full"
                      opacity={isActive ? 1 : 0.55}
                      transition="opacity 0.3s ease"
                    >
                      0{idx + 1}
                    </Badge>
                  </HStack>
                  <Box
                    position="absolute"
                    bottom={0}
                    left={0}
                    right={0}
                    h="2px"
                    overflow="hidden"
                  >
                    {isActive && !isPaused && (
                      <Box
                        key={`progress-${activeIndex}`}
                        h="full"
                        bg={accent.base}
                        sx={{
                          animation: `${pillarFill} ${FOUR_PILLARS_ROTATE_MS}ms linear forwards`,
                        }}
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
          </VStack>
        </Box>

        <Box
          gridColumn={{ lg: 'span 7' }}
          position="relative"
          minH={{ base: '520px', md: '540px', lg: '560px' }}
        >
          {FOUR_PILLARS.map((pillar, idx) => {
            const isActive = idx === activeIndex;
            const accent = PILLAR_ACCENTS[pillar.accent];
            const Visual = PILLAR_VISUALS[idx];
            return (
              <Box
                key={pillar.title}
                position="absolute"
                inset={0}
                opacity={isActive ? 1 : 0}
                transform={isActive ? 'translateY(0)' : 'translateY(14px)'}
                pointerEvents={isActive ? 'auto' : 'none'}
                transition="opacity 0.55s ease, transform 0.55s ease"
                aria-hidden={!isActive}
              >
                <Flex
                  direction="column"
                  bg="white"
                  borderRadius="3xl"
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow={CARD_SHADOW_HOVER}
                  overflow="hidden"
                  h="full"
                >
                  <Box
                    position="relative"
                    h={{ base: '220px', md: '240px' }}
                    bg={accent.gradient}
                    overflow="hidden"
                    flexShrink={0}
                  >
                    <Box
                      position="absolute"
                      inset={0}
                      bgImage="radial-gradient(circle at 20% 80%, rgba(255,255,255,0.55) 0, transparent 40%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.35) 0, transparent 45%)"
                      opacity={0.9}
                    />
                    <Box position="absolute" inset={0} p={{ base: 6, md: 8 }} pt={{ base: 12, md: 14 }}>
                      <Visual accent={accent} />
                    </Box>
                    <Badge
                      position="absolute"
                      top={4}
                      left={4}
                      bg="rgba(255,255,255,0.22)"
                      color="white"
                      border="1px solid"
                      borderColor="rgba(255,255,255,0.4)"
                      backdropFilter="blur(8px)"
                      px={3}
                      py={1}
                      borderRadius="full"
                      fontSize="xs"
                      fontFamily="mono"
                      letterSpacing="wider"
                      textTransform="none"
                    >
                      PILLAR 0{idx + 1} / 04
                    </Badge>
                  </Box>
                  <VStack
                    align="flex-start"
                    spacing={5}
                    p={{ base: 6, md: 8 }}
                    flex="1"
                  >
                    <Heading
                      as="h3"
                      size="lg"
                      color={BRAND.ink}
                      fontWeight="extrabold"
                      letterSpacing="-0.02em"
                    >
                      {pillar.title}
                    </Heading>
                    <Text
                      color="#475569"
                      fontSize={{ base: 'md', md: 'lg' }}
                      lineHeight={1.7}
                    >
                      {pillar.description}
                    </Text>
                    <Button
                      onClick={onCta}
                      bg={accent.gradient}
                      color="white"
                      borderRadius="full"
                      size="lg"
                      px={6}
                      rightIcon={<Icon as={FaArrowRight} boxSize={3.5} />}
                      _hover={{
                        transform: 'translateY(-1px)',
                        boxShadow: `0 12px 28px ${accent.glow}`,
                        filter: 'brightness(1.05)',
                      }}
                      _active={{ transform: 'scale(0.98)' }}
                      transition="all 0.2s ease"
                    >
                      Whitelabel my agency
                    </Button>
                  </VStack>
                </Flex>
              </Box>
            );
          })}
        </Box>
      </SimpleGrid>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Hero carousel slides
// ---------------------------------------------------------------------------
// The hero rotates through a 2-step narrative of the agency pitch:
//   1. "Where They Are"        — show the prospect's weak baseline
//   2. "Where You'll Take Them" — same module, dramatically better numbers
const HERO_SLIDES = [
  {
    id: 'where-they-are',
    eyebrow: 'Slide 1 of 2 · Diagnosis',
    title: 'Where They Are',
    subtitle:
      'Open with the truth. Pull up their market and show the ranks they have never seen before.',
    icon: FaMapMarkerAlt,
    accent: '#ff7373',
  },
  {
    id: 'where-youll-take-them',
    eyebrow: 'Slide 2 of 2 · Transformation',
    title: "Where You'll Take Them",
    subtitle:
      "Same module, the post-engagement numbers. Top-of-market sentiment, traffic, and review velocity.",
    icon: FaRocket,
    accent: '#7cb8ff',
  },
];

const HeroSlideNavButton = ({ direction, onClick, label, tone = 'dark' }) => {
  const onLight = tone === 'light';
  return (
    <Button
      onClick={onClick}
      aria-label={label}
      bg={onLight ? 'white' : 'whiteAlpha.50'}
      color={onLight ? BRAND.ink : 'white'}
      borderRadius="full"
      minW="40px"
      h="40px"
      p={0}
      borderWidth="1px"
      borderColor={onLight ? 'gray.200' : 'whiteAlpha.200'}
      _hover={{
        bg: onLight ? 'gray.50' : 'whiteAlpha.200',
        transform: 'translateY(-1px)',
      }}
      _active={{ bg: onLight ? 'gray.100' : 'whiteAlpha.300', transform: 'scale(0.96)' }}
      transition="all 0.2s ease-out"
    >
      <Icon as={direction === 'prev' ? FaChevronLeft : FaArrowRight} boxSize={3.5} />
    </Button>
  );
};

// Continuous progress bar replaces the plain pagination dots. Each segment
// renders as a thin track with a filled segment for the active slide and
// a subtle fade for the rest.
const HeroSlideProgress = ({ count, activeIndex, onSelect, tone = 'dark' }) => {
  const onLight = tone === 'light';
  return (
    <HStack spacing={1.5} align="center" flex="1" minW={0}>
      {Array.from({ length: count }).map((_, idx) => {
        const isActive = idx === activeIndex;
        const isPast = idx < activeIndex;
        return (
          <Box
            key={idx}
            as="button"
            onClick={() => onSelect(idx)}
            aria-label={`Go to slide ${idx + 1}`}
            flex="1"
            h="3px"
            borderRadius="full"
            bg={onLight ? 'gray.200' : 'whiteAlpha.200'}
            overflow="hidden"
            position="relative"
          >
            <Box
              position="absolute"
              inset={0}
              bg={
                isActive
                  ? onLight
                    ? BRAND.primary
                    : 'white'
                  : isPast
                    ? onLight
                      ? 'gray.400'
                      : 'whiteAlpha.700'
                    : 'transparent'
              }
              transform={isActive ? 'scaleX(1)' : isPast ? 'scaleX(1)' : 'scaleX(0)'}
              transformOrigin="left center"
              transition="transform 0.4s ease-out, background 0.3s ease-out"
            />
          </Box>
        );
      })}
    </HStack>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const AgencyLanding = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [heroSlideIndex, setHeroSlideIndex] = useState(0);
  // Unscaled layout height of the hero zoom wrapper. CSS `transform: scale`
  // is a visual-only effect so the bottom of the module sticks out past its
  // layout box once scaled. We measure the unscaled height here so we can
  // reserve enough vertical space below it to prevent the parent hero's
  // `overflow: hidden` from clipping the bottom of the module.
  const [heroModuleHeight, setHeroModuleHeight] = useState(0);
  // Ref points at the actively scaled slide wrapper. The scroll-linked
  // zoom is applied via a direct DOM write on this ref (see the scroll
  // effect below) — using a ref instead of React state keeps every scroll
  // frame off the React render path so the giant AgencyLanding tree
  // doesn't re-render at 60fps, which was the source of the visible
  // flicker/jank on the hero dashboard preview.
  const heroModuleRef = useRef(null);
  // Controls the "peek" treatment over the hero dashboard module. Default
  // is collapsed so the prospect sees roughly the top quarter of the
  // dashboard with a blurred fade and a "Show full preview" affordance —
  // this keeps the hero from eating the entire landing page and lets the
  // rest of the page (data sources, calculators, pillars, FAQ) get seen
  // without forcing the visitor to scroll past a giant card first.
  const [isHeroExpanded, setIsHeroExpanded] = useState(false);
  const [activeCalc, setActiveCalc] = useState('discovery');

  const [calc, setCalc] = useState({
    callsPerMonth: 20,
    currentCloseRate: 20,
    projectedCloseRate: 35,
    avgDealValue: 2000,
    upsellClients: 15,
    avgRetainer: 2000,
    upsellPercent: 30,
    avgUpsellValue: 500,
    churnClients: 20,
    churnRetainer: 2000,
    currentChurnRate: 5,
    targetChurnRate: 2,
  });

  const updateCalc = (key, value) => {
    setCalc((prev) => ({ ...prev, [key]: value === '' ? '' : Number(value) }));
  };

  const cv = (key) => Number(calc[key]) || 0;
  const fmtMoney = (n) => `$${Math.round(n).toLocaleString()}`;

  const dcCurrentClients = (cv('callsPerMonth') * cv('currentCloseRate')) / 100;
  const dcProjectedClients = (cv('callsPerMonth') * cv('projectedCloseRate')) / 100;
  const dcAdditionalRevenue = Math.max(
    0,
    (dcProjectedClients - dcCurrentClients) * cv('avgDealValue') * 12,
  );

  const usClientsUpsold = Math.round((cv('upsellClients') * cv('upsellPercent')) / 100);
  const usMonthlyUpsell = usClientsUpsold * cv('avgUpsellValue');
  const usAnnualUpsell = usMonthlyUpsell * 12;

  const chAnnualChurnCurrent = 1 - Math.pow(1 - Math.min(cv('currentChurnRate'), 100) / 100, 12);
  const chAnnualChurnTarget = 1 - Math.pow(1 - Math.min(cv('targetChurnRate'), 100) / 100, 12);
  const chClientsLostCurrent = cv('churnClients') * chAnnualChurnCurrent;
  const chClientsLostTarget = cv('churnClients') * chAnnualChurnTarget;
  const chClientsSaved = Math.max(0, chClientsLostCurrent - chClientsLostTarget);
  const chRevenueSaved = chClientsSaved * cv('churnRetainer') * 12;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Maximum extra scale applied to the dashboard module as the user
  // scrolls past the hero. Kept small (4%) so the growth is subtle and
  // can't introduce visible layout pressure. The ramp is also stretched
  // over ~1.6 viewport heights below to slow the motion further.
  const HERO_MODULE_MAX_ZOOM = 0.04;
  const HERO_MODULE_RAMP_VIEWPORTS = 1.6;

  // Drive the BusinessReviewModule's scroll-linked zoom directly on the
  // DOM via the ref. We deliberately do NOT use React state for the
  // scroll progress: pushing state on every rAF tick would re-render the
  // whole AgencyLanding tree at 60fps, and combining that with the CSS
  // `transition` on the scaled element produced a constant chase
  // animation that read as flicker. Writing `style.transform` directly
  // with no transition makes the scale track the scroll position
  // exactly, so the growth feels slow and steady instead of jittery.
  useEffect(() => {
    const node = heroModuleRef.current;
    if (isHeroExpanded) {
      if (node) node.style.transform = 'scale(1)';
      return undefined;
    }

    let frame = null;
    let lastApplied = -1;
    const apply = () => {
      frame = null;
      if (!heroModuleRef.current) return;
      const viewport = window.innerHeight || 1;
      const scrolled = window.scrollY || window.pageYOffset || 0;
      const progress = Math.max(
        0,
        Math.min(1, scrolled / (viewport * HERO_MODULE_RAMP_VIEWPORTS)),
      );
      // Quantize to 4 decimals so identical neighboring scroll positions
      // don't re-trigger style writes (which can otherwise nudge the
      // compositor) and to keep the transform output deterministic.
      const next = Math.round((1 + progress * HERO_MODULE_MAX_ZOOM) * 10000) / 10000;
      if (next === lastApplied) return;
      lastApplied = next;
      heroModuleRef.current.style.transform = `scale(${next})`;
    };
    const onScroll = () => {
      if (frame != null) return;
      frame = window.requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (frame != null) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
    // Re-bind whenever the active slide changes so the ref points at the
    // current scaled element and we re-apply the latest scroll position
    // immediately (otherwise the freshly-active slide would render at
    // scale(1) until the user scrolled again).
  }, [heroSlideIndex, isHeroExpanded]);

  // When the hero is collapsed we show roughly the top quarter of the
  // dashboard. We use the measured module height when available so the
  // peek scales with whatever variant is currently selected, and fall
  // back to a sensible default for the first paint before measurement.
  const HERO_COLLAPSED_FALLBACK_PX = 360;
  const heroCollapsedHeight =
    heroModuleHeight > 0
      ? Math.max(280, Math.round(heroModuleHeight * 0.25))
      : HERO_COLLAPSED_FALLBACK_PX;

  // Keep the unscaled height in sync with the rendered module. We read
  // `offsetHeight` (which ignores CSS transforms) so the reserved space
  // tracks real layout height across viewport resizes and slide swaps.
  useEffect(() => {
    const node = heroModuleRef.current;
    if (!node) return undefined;
    const update = () => {
      if (heroModuleRef.current) {
        setHeroModuleHeight(heroModuleRef.current.offsetHeight);
      }
    };
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [heroSlideIndex]);

  const scrollToSignup = () => {
    const bar = document.getElementById('agency-signup-form');
    if (bar) {
      const urlInput = bar.querySelector(
        '[data-testid="agency-floating-url-input"], [data-testid="agency-floating-name-input"]',
      );
      if (urlInput) {
        urlInput.focus({ preventScroll: false });
        return;
      }
      bar.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet>
        <title>Localytics for Agencies | Win Clients by Blowing Their Mind with Data</title>
        <meta
          name="description"
          content="Win more clients and upsell existing ones with whitelabeled competitive intelligence dashboards that show them data they've never seen before. All public data. No APIs required."
        />
        <meta
          property="og:title"
          content="Localytics for Agencies | Win More Clients by Blowing Their Mind"
        />
        <meta
          property="og:description"
          content="Show prospects and clients data they've never seen. All public data. No APIs, Google Analytics, or plugins required."
        />
        <meta property="og:url" content="https://localytics.co/agencies" />
      </Helmet>

      <Box
        minH="100vh"
        bg="white"
        pb={
          isHeroExpanded
            ? { base: 6, md: 8 }
            : { base: '160px', md: '120px' }
        }
      >
        {/* Sticky glassmorphic header sits at the page level so it can
            stay pinned past the hero. Lives outside the hero Box because
            the hero uses overflow:hidden which would clip the sticky. */}
        <LandingHeader
          onCtaClick={scrollToSignup}
          logoTo="/agencies"
          ctaLabel="Whitelabel My Agency"
          ctaFallbackPath="/agencies"
          showWordmark
          glassmorphic
          compact
          floatLogo
        />

        {/* ============================================================ */}
        {/* HERO                                                        */}
        {/* ============================================================ */}
        <Box
          position="relative"
          bg={BRAND.heroBg}
          overflowX="hidden"
          overflowY="visible"
        >
          <Box
            position="absolute"
            top="-20%"
            right="-10%"
            w={{ base: '300px', lg: '600px' }}
            h={{ base: '300px', lg: '600px' }}
            borderRadius="full"
            bg="radial-gradient(circle, rgba(29,110,245,0.18) 0%, transparent 70%)"
            pointerEvents="none"
          />
          <Box
            position="absolute"
            bottom="-15%"
            left="-8%"
            w={{ base: '250px', lg: '500px' }}
            h={{ base: '250px', lg: '500px' }}
            borderRadius="full"
            bg="radial-gradient(circle, rgba(59,166,245,0.12) 0%, transparent 70%)"
            pointerEvents="none"
          />

          <Container
            maxW="100%"
            position="relative"
            zIndex={1}
            px={{ base: 4, md: 6, lg: 10 }}
            pt={{ base: 10, md: 14, lg: 16 }}
            pb={isHeroExpanded ? { base: 4, md: 6 } : { base: 10, md: 14, lg: 16 }}
          >
            <VStack spacing={{ base: 10, md: 14 }} align="stretch">
              <VStack
                spacing={{ base: 6, md: 8 }}
                align="center"
                textAlign="center"
                maxW={{ base: '900px', lg: '1080px' }}
                mx="auto"
                position="relative"
                py={{ base: 2, md: 6, lg: 8 }}
              >
                {/* Soft radial glow behind the headline so the gradient
                    phrase reads as illuminated rather than flat. */}
                <Box
                  position="absolute"
                  top="20%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  w={{ base: '320px', md: '560px' }}
                  h={{ base: '220px', md: '320px' }}
                  bg="rgba(59, 130, 246, 0.20)"
                  filter="blur(64px)"
                  borderRadius="full"
                  pointerEvents="none"
                  zIndex={0}
                />

                <AnimatedBox animation="fadeIn" delay={0.1} position="relative" zIndex={1}>
                  <HStack
                    spacing={2}
                    bg="whiteAlpha.100"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                    color="#7cc4ff"
                    px={4}
                    py={1.5}
                    borderRadius="full"
                    fontSize="xs"
                    fontWeight="medium"
                    letterSpacing="0.2em"
                    textTransform="uppercase"
                  >
                    <Box
                      w="6px"
                      h="6px"
                      borderRadius="full"
                      bg="#7cc4ff"
                      boxShadow="0 0 8px rgba(124,196,255,0.8)"
                    />
                    <Text as="span">For Agencies &amp; Consultants</Text>
                  </HStack>
                </AnimatedBox>

                <AnimatedBox animation="slideUp" delay={0.2} position="relative" zIndex={1}>
                  <Heading
                    as="h1"
                    fontSize={{ base: '6xl', md: '7xl', lg: '8xl' }}
                    color="white"
                    lineHeight={{ base: 1.1, md: 1.15 }}
                    fontWeight="extrabold"
                    letterSpacing="-0.03em"
                  >
                    Give this exact PDF to{' '}
                    <GradientText>potential clients</GradientText>
                    .
                  </Heading>
                </AnimatedBox>

                <AnimatedBox animation="slideUp" delay={0.3} position="relative" zIndex={1}>
                  <Text
                    fontSize={{ base: 'xl', md: '2xl', lg: '3xl' }}
                    color="whiteAlpha.800"
                    maxW={{ base: '680px', lg: '820px' }}
                    lineHeight={1.6}
                    mt={{ base: 3, md: 4 }}
                  >
                    Whitelabeled, instantly &mdash; drop any URL and we build the same
                    report for your prospect.
                  </Text>
                </AnimatedBox>
              </VStack>
            </VStack>
          </Container>
        </Box>

        {/* Dashboard preview — navy when collapsed; light gray when expanded so
            scrolling past the module does not leave a long empty navy band
            above the floating prospect bar. */}
        <Box
          position="relative"
          bg={isHeroExpanded ? 'gray.50' : BRAND.heroBg}
          overflowX="hidden"
          overflowY="visible"
          pb={{ base: isHeroExpanded ? 6 : 10, md: isHeroExpanded ? 8 : 14 }}
        >
          {!isHeroExpanded && (
            <>
              <Box
                position="absolute"
                top="-10%"
                right="-10%"
                w={{ base: '260px', lg: '480px' }}
                h={{ base: '260px', lg: '480px' }}
                borderRadius="full"
                bg="radial-gradient(circle, rgba(29,110,245,0.14) 0%, transparent 70%)"
                pointerEvents="none"
              />
              <Box
                position="absolute"
                bottom="-12%"
                left="-8%"
                w={{ base: '220px', lg: '420px' }}
                h={{ base: '220px', lg: '420px' }}
                borderRadius="full"
                bg="radial-gradient(circle, rgba(59,166,245,0.1) 0%, transparent 70%)"
                pointerEvents="none"
              />
            </>
          )}

          <Container
            maxW="100%"
            position="relative"
            zIndex={1}
            px={{ base: 4, md: 6, lg: 10 }}
            pt={isHeroExpanded ? { base: 2, md: 4 } : 0}
          >
            <Box w="100%">
                  {(() => {
                    const activeSlide = HERO_SLIDES[heroSlideIndex] || HERO_SLIDES[0];
                    const railTone = isHeroExpanded ? 'light' : 'dark';
                    const goToSlide = (idx) => {
                      const n = HERO_SLIDES.length;
                      setHeroSlideIndex(((idx % n) + n) % n);
                    };
                    // Each slide renders at SLIDE_WIDTH_PCT% of the carousel
                    // viewport. The remaining sliver leaks the next slide
                    // into view on the right so the prospect can SEE there's
                    // more content and click into it. The active slide is
                    // pinned to the left so the peek always lives on the
                    // right edge — that's the affordance the user asked for.
                    const SLIDE_WIDTH_PCT = 92;
                    const SLIDE_GAP_PX = 16;
                    const isLast = heroSlideIndex === HERO_SLIDES.length - 1;
                    const nextSlide = HERO_SLIDES[heroSlideIndex + 1];
                    return (
                      <Flex
                        direction={{ base: 'column', lg: 'row' }}
                        align={{ base: 'stretch', lg: 'flex-start' }}
                        gap={{ base: 6, lg: 5, xl: 6 }}
                        w="100%"
                        data-testid="agency-hero-module-row"
                      >
                        <AnimatedBox
                          animation="scale"
                          delay={0.35}
                          flex="1"
                          minW={0}
                          w="100%"
                        >
                        {/* Carousel viewport: all 3 slides are mounted in a
                            horizontal track so the active one is centered
                            and the next/previous slides peek in from the
                            sides. The peeking edges are clickable so the
                            prospect can advance the carousel just by
                            tapping the visible sliver. */}
                        <Box
                          position="relative"
                          w="100%"
                          overflow="hidden"
                          pb={0}
                          maxH={
                            isHeroExpanded ? 'none' : `${heroCollapsedHeight}px`
                          }
                          transition="max-height 0.55s cubic-bezier(0.22, 1, 0.36, 1), padding-bottom 0.4s ease-out"
                          data-testid="agency-hero-carousel"
                          aria-expanded={isHeroExpanded}
                          zIndex={1}
                        >
                          <Flex
                            align="flex-start"
                            transform={`translateX(calc(-1 * ${heroSlideIndex} * (${SLIDE_WIDTH_PCT}% + ${SLIDE_GAP_PX}px)))`}
                            transition="transform 0.55s cubic-bezier(0.22, 1, 0.36, 1)"
                            style={{
                              gap: `${SLIDE_GAP_PX}px`,
                              willChange: 'transform',
                            }}
                          >
                            {HERO_SLIDES.map((slide, idx) => {
                              const isActive = idx === heroSlideIndex;
                              return (
                                <Box
                                  key={slide.id}
                                  flex={`0 0 ${SLIDE_WIDTH_PCT}%`}
                                  opacity={isActive ? 1 : 0.45}
                                  transition="opacity 0.45s ease-out, filter 0.45s ease-out"
                                  filter={isActive ? 'none' : 'saturate(0.85)'}
                                  cursor={isActive ? 'default' : 'pointer'}
                                  onClick={() => {
                                    if (!isActive) goToSlide(idx);
                                  }}
                                  role={!isActive ? 'button' : undefined}
                                  aria-label={
                                    !isActive
                                      ? `Go to slide ${idx + 1}: ${slide.title}`
                                      : undefined
                                  }
                                  data-testid={`agency-hero-slide-${slide.id}`}
                                >
                                  <Box
                                    ref={isActive ? heroModuleRef : null}
                                    data-testid={
                                      isActive
                                        ? 'agency-hero-module-zoom-wrapper'
                                        : undefined
                                    }
                                    pointerEvents={isActive ? 'auto' : 'none'}
                                    style={{
                                      // Active slide: scroll-linked scale
                                      // is written directly to this node
                                      // via the ref-based effect above, so
                                      // we leave transform unset here.
                                      // Inactive slides stay at a static
                                      // smaller scale to read as "behind"
                                      // the active one. Using a transition
                                      // only on the inactive baseline
                                      // keeps slide swaps smooth without
                                      // letting the transition fight the
                                      // continuous scroll-driven update.
                                      transform: isActive
                                        ? undefined
                                        : 'scale(0.96)',
                                      transformOrigin: 'center top',
                                      transition: isActive
                                        ? 'none'
                                        : 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)',
                                      willChange: 'transform',
                                    }}
                                  >
                                    <AgencyHeroReviewModule variant={slide.id} />
                                  </Box>
                                </Box>
                              );
                            })}
                          </Flex>

                          {/* Collapsed-state peek: anchored to the carousel
                              viewport (not the full slide height) so the blur
                              and expand button stay visible above overflow
                              clipping when maxH limits the preview. */}
                          {!isHeroExpanded && (
                            <Box
                              position="absolute"
                              left={0}
                              bottom={0}
                              w={`${SLIDE_WIDTH_PCT}%`}
                              h={{ base: '70%', md: '65%' }}
                              display="flex"
                              alignItems="flex-end"
                              justifyContent="center"
                              pb={{ base: 5, md: 7 }}
                              pointerEvents="none"
                              zIndex={10}
                              maxW={{
                                base: '100%',
                                md: '100%',
                                lg: '1280px',
                                xl: '1440px',
                                '2xl': '1600px',
                              }}
                              mx="auto"
                              bgGradient="linear(to-t, rgba(10,24,53,0.92) 30%, rgba(10,24,53,0.75) 55%, rgba(10,24,53,0) 100%)"
                              sx={{
                                backdropFilter: 'blur(6px)',
                                WebkitBackdropFilter: 'blur(6px)',
                                maskImage:
                                  'linear-gradient(to top, rgba(0,0,0,1) 55%, rgba(0,0,0,0) 100%)',
                                WebkitMaskImage:
                                  'linear-gradient(to top, rgba(0,0,0,1) 55%, rgba(0,0,0,0) 100%)',
                              }}
                              data-testid="agency-hero-peek-overlay"
                            >
                              <Button
                                onClick={() => setIsHeroExpanded(true)}
                                size="lg"
                                fontWeight="700"
                                px={7}
                                py={6}
                                borderRadius="full"
                                bg="white"
                                color={BRAND.deep}
                                boxShadow="0 18px 48px -12px rgba(10,24,53,0.65), 0 4px 12px rgba(10,24,53,0.35)"
                                pointerEvents="auto"
                                _hover={{
                                  bg: 'white',
                                  transform: 'translateY(-1px)',
                                  boxShadow:
                                    '0 22px 56px -12px rgba(10,24,53,0.7), 0 6px 14px rgba(10,24,53,0.4)',
                                }}
                                _active={{ transform: 'translateY(0)' }}
                                rightIcon={<Icon as={FaChevronDown} />}
                                data-testid="agency-hero-expand-button"
                              >
                                Show full preview
                              </Button>
                            </Box>
                          )}

                          {/* Soft fade + click target on the right edge so
                              the peeking sliver visually reads as "there's
                              more — tap to advance." Only rendered when
                              there's a next slide to advance to. */}
                          {!isLast && isHeroExpanded && (
                            <Box
                              as="button"
                              type="button"
                              position="absolute"
                              right={0}
                              top={0}
                              bottom={0}
                              w={{ base: '32px', md: '56px' }}
                              cursor="pointer"
                              onClick={() => goToSlide(heroSlideIndex + 1)}
                              aria-label={
                                nextSlide
                                  ? `Next slide: ${nextSlide.title}`
                                  : 'Next slide'
                              }
                              data-testid="agency-hero-peek-next"
                              bgGradient="linear(to-l, rgba(10,24,53,0.6), rgba(10,24,53,0))"
                              transition="opacity 0.2s ease-out, background 0.2s ease-out"
                              opacity={0.9}
                              _hover={{ opacity: 1 }}
                            />
                          )}
                        </Box>
                        </AnimatedBox>

                        {/* Slide caption, carousel nav, and CTA — pinned to the
                            right of the dashboard preview. Stays fixed in
                            view while scrolling through the hero until the
                            data-sources section begins. */}
                        <Box
                          position={{ base: 'relative', lg: 'sticky' }}
                          top={{ lg: '72px' }}
                          alignSelf="flex-start"
                          w={{ base: '100%', lg: '240px', xl: '280px' }}
                          flexShrink={0}
                          zIndex={5}
                          data-testid="agency-hero-slide-rail"
                          bg={isHeroExpanded ? 'white' : 'transparent'}
                          borderRadius={isHeroExpanded ? '2xl' : undefined}
                          border={isHeroExpanded ? '1px solid' : undefined}
                          borderColor={isHeroExpanded ? 'gray.200' : undefined}
                          boxShadow={
                            isHeroExpanded
                              ? '0 12px 40px rgba(10,31,68,0.08)'
                              : undefined
                          }
                          p={isHeroExpanded ? { base: 4, md: 5 } : 0}
                        >
                          <VStack spacing={5} align="stretch">
                            <Box
                              key={activeSlide.id}
                              data-testid="agency-hero-slide-heading"
                            >
                              <AnimatedBox
                                animation="fadeIn"
                                delay={0}
                                duration={0.35}
                              >
                                <VStack spacing={3} align="flex-start">
                                  <Flex
                                    w="44px"
                                    h="44px"
                                    borderRadius="xl"
                                    align="center"
                                    justify="center"
                                    flexShrink={0}
                                    bg={
                                      isHeroExpanded ? 'gray.50' : 'whiteAlpha.200'
                                    }
                                    color={activeSlide.accent}
                                    border="1px solid"
                                    borderColor={
                                      isHeroExpanded ? 'gray.200' : 'whiteAlpha.300'
                                    }
                                  >
                                    <Icon as={activeSlide.icon} boxSize={5} />
                                  </Flex>
                                  <VStack spacing={0.5} align="flex-start">
                                    <Text
                                      fontSize="xs"
                                      fontWeight="700"
                                      color={
                                        isHeroExpanded ? 'gray.500' : 'whiteAlpha.700'
                                      }
                                      textTransform="uppercase"
                                      letterSpacing="0.14em"
                                    >
                                      {activeSlide.eyebrow}
                                    </Text>
                                    <Heading
                                      as="h2"
                                      fontSize={{ base: 'lg', md: 'xl' }}
                                      color={isHeroExpanded ? BRAND.ink : 'white'}
                                      fontWeight="extrabold"
                                      lineHeight="1.2"
                                      letterSpacing="-0.01em"
                                    >
                                      {activeSlide.title}
                                    </Heading>
                                    <Text
                                      fontSize={{ base: 'xs', md: 'sm' }}
                                      color={
                                        isHeroExpanded ? 'gray.600' : 'whiteAlpha.800'
                                      }
                                      lineHeight={1.5}
                                    >
                                      {activeSlide.subtitle}
                                    </Text>
                                  </VStack>
                                </VStack>
                              </AnimatedBox>
                            </Box>

                            <HStack spacing={2} align="center" w="100%">
                              <HeroSlideNavButton
                                direction="prev"
                                label="Previous slide"
                                tone={railTone}
                                onClick={() => goToSlide(heroSlideIndex - 1)}
                              />
                              <HeroSlideProgress
                                count={HERO_SLIDES.length}
                                activeIndex={heroSlideIndex}
                                tone={railTone}
                                onSelect={goToSlide}
                              />
                              <HeroSlideNavButton
                                direction="next"
                                label="Next slide"
                                tone={railTone}
                                onClick={() => goToSlide(heroSlideIndex + 1)}
                              />
                            </HStack>

                            {isHeroExpanded ? (
                              <Button
                                onClick={() => setIsHeroExpanded(false)}
                                w="100%"
                                size="md"
                                variant="outline"
                                borderColor="gray.300"
                                color={BRAND.ink}
                                fontWeight="semibold"
                                borderRadius="full"
                                leftIcon={<Icon as={FaChevronDown} boxSize={3} sx={{ transform: 'rotate(180deg)' }} />}
                                data-testid="agency-hero-collapse-button"
                              >
                                Collapse preview
                              </Button>
                            ) : null}

                            <Button
                              onClick={scrollToSignup}
                              w="100%"
                              size="lg"
                              fontWeight="bold"
                              borderRadius="full"
                              bg={BRAND.primary}
                              color="white"
                              rightIcon={<Icon as={FaArrowRight} boxSize={3.5} />}
                              boxShadow="0 12px 32px rgba(29,110,245,0.45)"
                              _hover={{
                                bg: BRAND.primary,
                                filter: 'brightness(1.06)',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 16px 40px rgba(29,110,245,0.55)',
                              }}
                              _active={{ transform: 'translateY(0)' }}
                              transition="all 0.2s ease-out"
                              data-testid="agency-hero-rail-cta"
                            >
                              Whitelabel My Agency
                            </Button>
                          </VStack>
                        </Box>
                      </Flex>
                    );
                  })()}
            </Box>
          </Container>
        </Box>

        {/* ============================================================ */}
        {/* WHERE THE DATA COMES FROM                                   */}
        {/* ============================================================ */}
        {/* Transparency block: separates hard data (real, scraped         */}
        {/* reviews) from soft data (SpyFu/Semrush estimates and AI        */}
        {/* derivatives). Prospects and agencies repeatedly ask "where    */}
        {/* does this number come from?" — answering it inline removes   */}
        {/* the objection before the calculator / signup. */}
        <Box py={SECTION_PADDING} bg="white" id="data-sources">
          <Container maxW={CONTAINER_MAX}>
            <VStack spacing={12} align="stretch">
              <SectionHeader
                eyebrow="Where the data comes from"
                title="Real data first."
                accent="Smart estimates on top."
                subtitle="Every dashboard mixes hard facts you can verify with calibrated estimates that fill in the rest. Here's exactly what's what — and where each number is sourced."
              />

              {/* Hard data hero panel — reviews are the foundation. We
                  give it the prominent, full-width slot so it visibly
                  outweighs the soft-data tiles below. */}
              <AnimatedBox animation="slideUp" delay={0.1}>
                <Box
                  position="relative"
                  bg="white"
                  borderRadius="3xl"
                  border="1px solid"
                  borderColor="green.100"
                  boxShadow="0 4px 12px rgba(10,31,68,0.04), 0 24px 60px rgba(10,31,68,0.08)"
                  overflow="hidden"
                >
                  {/* Soft green wash + radial glow so the hard-data
                      panel reads as the "trusted, anchored" source. */}
                  <Box
                    position="absolute"
                    inset={0}
                    bgGradient="linear(to-br, green.50, white 60%)"
                    pointerEvents="none"
                  />
                  <Box
                    position="absolute"
                    top="-15%"
                    right="-8%"
                    w={{ base: '260px', md: '420px' }}
                    h={{ base: '260px', md: '420px' }}
                    borderRadius="full"
                    bg="radial-gradient(circle, rgba(34,197,94,0.18) 0%, transparent 70%)"
                    pointerEvents="none"
                  />

                  <SimpleGrid
                    columns={{ base: 1, lg: 5 }}
                    spacing={0}
                    position="relative"
                    zIndex={1}
                  >
                    {/* LEFT: hard data story */}
                    <Box
                      gridColumn={{ lg: 'span 3' }}
                      p={{ base: 6, md: 10 }}
                    >
                      <VStack align="flex-start" spacing={5}>
                        <HStack spacing={3}>
                          <IconTile icon={FaCommentDots} accent="green" />
                          <Badge
                            bg="green.500"
                            color="white"
                            px={3}
                            py={1.5}
                            borderRadius="full"
                            fontSize="xs"
                            fontWeight="bold"
                            letterSpacing="0.08em"
                            textTransform="uppercase"
                          >
                            Hard Data · 100% Accurate
                          </Badge>
                        </HStack>
                        <Heading
                          as="h3"
                          fontSize={{ base: '2xl', md: '3xl' }}
                          color={BRAND.ink}
                          fontWeight="extrabold"
                          letterSpacing="-0.01em"
                          lineHeight={1.15}
                        >
                          Reviews — scraped, not estimated.
                        </Heading>
                        <Text color="#475569" fontSize={{ base: 'md', md: 'lg' }} lineHeight={1.65}>
                          Every review you see in the dashboard is a real,
                          word-for-word review we scrape directly from Google.
                          When we pull a market, we collect fresh reviews for
                          every competitor in about an hour — so the numbers
                          are accurate to the day, not sampled or modeled.
                        </Text>

                        <VStack align="flex-start" spacing={2.5} pt={1}>
                          {[
                            'Real text from real customers — every star, every word',
                            '~1 hour to refresh an entire market of competitors',
                            'Powers totals, sentiment, growth, SWOT, and pricing perception',
                          ].map((item) => (
                            <HStack key={item} align="flex-start" spacing={2.5}>
                              <Flex
                                w="20px"
                                h="20px"
                                justify="center"
                                align="center"
                                flexShrink={0}
                                mt="2px"
                              >
                                <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                              </Flex>
                              <Text fontSize={{ base: 'sm', md: 'md' }} color="#334155" lineHeight={1.55}>
                                {item}
                              </Text>
                            </HStack>
                          ))}
                        </VStack>
                      </VStack>
                    </Box>

                    {/* RIGHT: stat callouts so the "1 hour / 100% / every
                        competitor" claims land visually, not just in copy. */}
                    <Box
                      gridColumn={{ lg: 'span 2' }}
                      p={{ base: 6, md: 8 }}
                      borderTop={{ base: '1px solid', lg: 'none' }}
                      borderLeft={{ base: 'none', lg: '1px solid' }}
                      borderColor="green.100"
                      bg="whiteAlpha.700"
                    >
                      <VStack align="stretch" spacing={4} h="full" justify="center">
                        {[
                          {
                            icon: FaClock,
                            stat: '~1 hr',
                            label: 'Fresh market report per market',
                          },
                          {
                            icon: FaCheckCircle,
                            stat: '100%',
                            label: 'Accurate — every word is real',
                          },
                          {
                            icon: FaCommentDots,
                            stat: 'All',
                            label: 'Reviews, for every competitor',
                          },
                        ].map((row) => (
                          <HStack
                            key={row.label}
                            bg="white"
                            border="1px solid"
                            borderColor="green.100"
                            borderRadius="xl"
                            p={4}
                            spacing={4}
                            align="center"
                            boxShadow="0 1px 2px rgba(10,31,68,0.04)"
                          >
                            <Flex
                              w="40px"
                              h="40px"
                              borderRadius="lg"
                              bg="green.50"
                              color="green.600"
                              align="center"
                              justify="center"
                              flexShrink={0}
                            >
                              <Icon as={row.icon} boxSize={4} />
                            </Flex>
                            <VStack align="flex-start" spacing={0}>
                              <Text
                                fontSize={{ base: 'xl', md: '2xl' }}
                                fontWeight="extrabold"
                                color="green.700"
                                lineHeight={1}
                                letterSpacing="-0.01em"
                              >
                                {row.stat}
                              </Text>
                              <Text fontSize="xs" color="gray.600" lineHeight={1.4}>
                                {row.label}
                              </Text>
                            </VStack>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  </SimpleGrid>
                </Box>
              </AnimatedBox>

              {/* Second hard-data panel — Google PPC ads are observed
                  verbatim from live search results, not modeled. We give
                  it its own green-themed panel so prospects see we have
                  TWO sources of ground-truth data, not just reviews. */}
              <AnimatedBox animation="slideUp" delay={0.15}>
                <Box
                  position="relative"
                  bg="white"
                  borderRadius="3xl"
                  border="1px solid"
                  borderColor="green.100"
                  boxShadow="0 4px 12px rgba(10,31,68,0.04), 0 24px 60px rgba(10,31,68,0.08)"
                  overflow="hidden"
                >
                  <Box
                    position="absolute"
                    inset={0}
                    bgGradient="linear(to-br, green.50, white 60%)"
                    pointerEvents="none"
                  />
                  <Box
                    position="absolute"
                    top="-15%"
                    left="-8%"
                    w={{ base: '260px', md: '420px' }}
                    h={{ base: '260px', md: '420px' }}
                    borderRadius="full"
                    bg="radial-gradient(circle, rgba(34,197,94,0.18) 0%, transparent 70%)"
                    pointerEvents="none"
                  />

                  <SimpleGrid
                    columns={{ base: 1, lg: 5 }}
                    spacing={0}
                    position="relative"
                    zIndex={1}
                  >
                    {/* LEFT: stat callouts — flipped vs. the Reviews panel
                        so the two hard-data blocks visually rhyme without
                        feeling like a duplicate. */}
                    <Box
                      gridColumn={{ lg: 'span 2' }}
                      p={{ base: 6, md: 8 }}
                      borderBottom={{ base: '1px solid', lg: 'none' }}
                      borderRight={{ base: 'none', lg: '1px solid' }}
                      borderColor="green.100"
                      bg="whiteAlpha.700"
                    >
                      <VStack align="stretch" spacing={4} h="full" justify="center">
                        {[
                          {
                            icon: FaBullhorn,
                            stat: 'Live',
                            label: 'Ad copy, captured from Google',
                          },
                          {
                            icon: FaCheckCircle,
                            stat: '100%',
                            label: 'Real headlines, descriptions, and URLs',
                          },
                          {
                            icon: FaChartBar,
                            stat: 'Every',
                            label: 'Competitor PPC ad in the market',
                          },
                        ].map((row) => (
                          <HStack
                            key={row.label}
                            bg="white"
                            border="1px solid"
                            borderColor="green.100"
                            borderRadius="xl"
                            p={4}
                            spacing={4}
                            align="center"
                            boxShadow="0 1px 2px rgba(10,31,68,0.04)"
                          >
                            <Flex
                              w="40px"
                              h="40px"
                              borderRadius="lg"
                              bg="green.50"
                              color="green.600"
                              align="center"
                              justify="center"
                              flexShrink={0}
                            >
                              <Icon as={row.icon} boxSize={4} />
                            </Flex>
                            <VStack align="flex-start" spacing={0}>
                              <Text
                                fontSize={{ base: 'xl', md: '2xl' }}
                                fontWeight="extrabold"
                                color="green.700"
                                lineHeight={1}
                                letterSpacing="-0.01em"
                              >
                                {row.stat}
                              </Text>
                              <Text fontSize="xs" color="gray.600" lineHeight={1.4}>
                                {row.label}
                              </Text>
                            </VStack>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>

                    {/* RIGHT: hard data story for Google PPC ads */}
                    <Box
                      gridColumn={{ lg: 'span 3' }}
                      p={{ base: 6, md: 10 }}
                    >
                      <VStack align="flex-start" spacing={5}>
                        <HStack spacing={3}>
                          <IconTile icon={FaBullhorn} accent="green" />
                          <Badge
                            bg="green.500"
                            color="white"
                            px={3}
                            py={1.5}
                            borderRadius="full"
                            fontSize="xs"
                            fontWeight="bold"
                            letterSpacing="0.08em"
                            textTransform="uppercase"
                          >
                            Hard Data · 100% Accurate
                          </Badge>
                        </HStack>
                        <Heading
                          as="h3"
                          fontSize={{ base: '2xl', md: '3xl' }}
                          color={BRAND.ink}
                          fontWeight="extrabold"
                          letterSpacing="-0.01em"
                          lineHeight={1.15}
                        >
                          Google PPC ads — captured, not estimated.
                        </Heading>
                        <Text color="#475569" fontSize={{ base: 'md', md: 'lg' }} lineHeight={1.65}>
                          Every paid ad you see in the dashboard is the
                          real, word-for-word ad we pulled directly off
                          Google search results. Headlines, descriptions,
                          landing pages, and the keywords each competitor
                          is bidding on — captured exactly as a customer
                          would see them.
                        </Text>

                        <VStack align="flex-start" spacing={2.5} pt={1}>
                          {[
                            'Real ad headlines, descriptions, and destination URLs',
                            'Every PPC competitor running in the market — not a sample',
                            'Powers ad intelligence, keyword gaps, and bidding insights',
                          ].map((item) => (
                            <HStack key={item} align="flex-start" spacing={2.5}>
                              <Flex
                                w="20px"
                                h="20px"
                                justify="center"
                                align="center"
                                flexShrink={0}
                                mt="2px"
                              >
                                <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                              </Flex>
                              <Text fontSize={{ base: 'sm', md: 'md' }} color="#334155" lineHeight={1.55}>
                                {item}
                              </Text>
                            </HStack>
                          ))}
                        </VStack>
                      </VStack>
                    </Box>
                  </SimpleGrid>
                </Box>
              </AnimatedBox>

              {/* Soft data: a separator label so the visual hierarchy
                  clearly reads "the rest is estimated/derived." */}
              <VStack spacing={6} align="stretch">
                <HStack spacing={3} align="center" justify="center">
                  <Divider flex="1" borderColor="gray.200" />
                  <Badge
                    bg="blue.50"
                    color="blue.700"
                    px={3}
                    py={1.5}
                    borderRadius="full"
                    fontSize="xs"
                    fontWeight="bold"
                    letterSpacing="0.12em"
                    textTransform="uppercase"
                    border="1px solid"
                    borderColor="blue.100"
                    flexShrink={0}
                  >
                    Soft Data · Calibrated Estimates
                  </Badge>
                  <Divider flex="1" borderColor="gray.200" />
                </HStack>

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                  {SOFT_DATA_SOURCES.map((item, index) => (
                    <AnimatedBox
                      key={item.title}
                      animation="slideUp"
                      delay={0.15 + index * 0.08}
                    >
                      <Card>
                        <VStack align="flex-start" spacing={4} h="full">
                          <IconTile icon={item.icon} accent={item.accent} />
                          <Heading
                            as="h4"
                            size="md"
                            color={BRAND.ink}
                            fontWeight="bold"
                            letterSpacing="-0.01em"
                          >
                            {item.title}
                          </Heading>
                          <Text color="#475569" fontSize="md" lineHeight={1.65}>
                            {item.description}
                          </Text>
                          <Box flex="1" />
                          <Box
                            w="full"
                            pt={3}
                            borderTop="1px dashed"
                            borderColor="gray.200"
                          >
                            <Text
                              fontSize="xs"
                              fontWeight="bold"
                              color={`${item.accent}.600`}
                              letterSpacing="0.04em"
                            >
                              {item.source}
                            </Text>
                          </Box>
                        </VStack>
                      </Card>
                    </AnimatedBox>
                  ))}
                </SimpleGrid>
              </VStack>

              {/* Closing note — defuses the inevitable "but is the
                  traffic number exact?" question by reframing it. */}
              <Box
                bg="gray.50"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                p={6}
                maxW="820px"
                mx="auto"
                textAlign="center"
              >
                <HStack spacing={3} justify="center" align="flex-start">
                  <Flex
                    w="20px"
                    h="20px"
                    justify="center"
                    align="center"
                    flexShrink={0}
                    mt="3px"
                  >
                    <Icon as={FaLightbulb} color={BRAND.primary} boxSize={4} />
                  </Flex>
                  <Text fontSize={{ base: 'sm', md: 'md' }} color={BRAND.ink} lineHeight={1.6}>
                    <b>Use the hard data to anchor the conversation</b> — reviews,
                    sentiment, growth. Use the soft data to <i>rank</i> competitors
                    and spot opportunities, not to chase decimal points.
                  </Text>
                </HStack>
              </Box>
            </VStack>
          </Container>
        </Box>

        {/* ============================================================ */}
        {/* CALCULATORS                                                 */}
        {/* ============================================================ */}
        <Box py={SECTION_PADDING} bg="gray.50" id="calculators">
          <Container maxW={CONTAINER_MAX}>
            <VStack spacing={10} align="stretch">
              <SectionHeader
                eyebrow="Agency calculators"
                title="See the impact on"
                accent="your bottom line"
                subtitle="Pick what matters most to your agency. Plug in your numbers and watch the impact unfold."
              />

              {(() => {
                // Calculator definitions live inside the IIFE so the inline
                // result/breakdown rendering can reference the already-
                // computed values above without prop drilling.
                const CALC_TABS = [
                  {
                    id: 'discovery',
                    icon: FaPhoneAlt,
                    accent: 'blue',
                    title: 'Win More Deals',
                    short: 'Discovery Call Win Rate',
                    subtitle: 'Close more deals with competitive data',
                    headlineLabel: 'additional revenue per year',
                    headlineValue: `+${fmtMoney(dcAdditionalRevenue)}`,
                    inputs: [
                      {
                        k: 'callsPerMonth',
                        label: 'Discovery calls per month',
                        min: 0,
                        max: 200,
                        step: 1,
                        format: (v) => `${v}`,
                      },
                      {
                        k: 'currentCloseRate',
                        label: 'Current close rate (%)',
                        min: 0,
                        max: 100,
                        step: 1,
                        format: (v) => `${v}%`,
                      },
                      {
                        k: 'projectedCloseRate',
                        label: 'Projected close rate with data (%)',
                        min: 0,
                        max: 100,
                        step: 1,
                        format: (v) => `${v}%`,
                      },
                      {
                        k: 'avgDealValue',
                        label: 'Avg client value ($/month)',
                        min: 0,
                        max: 20000,
                        step: 100,
                        format: (v) => `$${Number(v).toLocaleString()}`,
                      },
                    ],
                    breakdown: [
                      {
                        label: 'Currently closing',
                        value: `${dcCurrentClients.toFixed(1)} clients/mo`,
                      },
                      {
                        label: 'With competitive data',
                        value: `${dcProjectedClients.toFixed(1)} clients/mo`,
                        highlight: true,
                      },
                    ],
                  },
                  {
                    id: 'upsell',
                    icon: FaArrowUp,
                    accent: 'green',
                    title: 'Upsell Existing Clients',
                    short: 'Upselling Clients',
                    subtitle: 'Turn competitive gaps into upsell revenue',
                    headlineLabel: 'upsell revenue per year',
                    headlineValue: `+${fmtMoney(usAnnualUpsell)}`,
                    inputs: [
                      {
                        k: 'upsellClients',
                        label: 'Current number of clients',
                        min: 0,
                        max: 200,
                        step: 1,
                        format: (v) => `${v}`,
                      },
                      {
                        k: 'avgRetainer',
                        label: 'Avg monthly retainer ($)',
                        min: 0,
                        max: 20000,
                        step: 100,
                        format: (v) => `$${Number(v).toLocaleString()}`,
                      },
                      {
                        k: 'upsellPercent',
                        label: 'Clients you can upsell (%)',
                        min: 0,
                        max: 100,
                        step: 1,
                        format: (v) => `${v}%`,
                      },
                      {
                        k: 'avgUpsellValue',
                        label: 'Avg upsell value ($/month)',
                        min: 0,
                        max: 10000,
                        step: 50,
                        format: (v) => `$${Number(v).toLocaleString()}`,
                      },
                    ],
                    breakdown: [
                      { label: 'Clients upsold', value: `${usClientsUpsold} clients` },
                      {
                        label: 'Monthly upsell revenue',
                        value: `${fmtMoney(usMonthlyUpsell)}/mo`,
                        highlight: true,
                      },
                    ],
                  },
                  {
                    id: 'churn',
                    icon: FaShieldAlt,
                    accent: 'purple',
                    title: 'Reduce Churn',
                    short: 'Reducing Churn',
                    subtitle: 'Keep clients longer with daily-use dashboards',
                    headlineLabel: 'revenue saved per year',
                    headlineValue: fmtMoney(chRevenueSaved),
                    inputs: [
                      {
                        k: 'churnClients',
                        label: 'Current number of clients',
                        min: 0,
                        max: 200,
                        step: 1,
                        format: (v) => `${v}`,
                      },
                      {
                        k: 'churnRetainer',
                        label: 'Avg monthly retainer ($)',
                        min: 0,
                        max: 20000,
                        step: 100,
                        format: (v) => `$${Number(v).toLocaleString()}`,
                      },
                      {
                        k: 'currentChurnRate',
                        label: 'Current monthly churn rate (%)',
                        min: 0,
                        max: 30,
                        step: 0.5,
                        format: (v) => `${Number(v).toFixed(1)}%`,
                      },
                      {
                        k: 'targetChurnRate',
                        label: 'Target churn rate with platform (%)',
                        min: 0,
                        max: 30,
                        step: 0.5,
                        format: (v) => `${Number(v).toFixed(1)}%`,
                      },
                    ],
                    breakdown: [
                      {
                        label: 'Clients lost/yr (now)',
                        value: chClientsLostCurrent.toFixed(1),
                      },
                      {
                        label: 'Clients lost/yr (target)',
                        value: chClientsLostTarget.toFixed(1),
                      },
                      {
                        label: 'Clients saved',
                        value: chClientsSaved.toFixed(1),
                        highlight: true,
                      },
                    ],
                  },
                ];

                const active =
                  CALC_TABS.find((t) => t.id === activeCalc) || CALC_TABS[0];

                return (
                  <VStack spacing={8} align="stretch">
                    {/* Segmented selector: pick which calculator to focus on */}
                    <Box>
                      <Text
                        textAlign="center"
                        fontSize="xs"
                        fontWeight="bold"
                        color="gray.500"
                        letterSpacing="0.18em"
                        textTransform="uppercase"
                        mb={3}
                      >
                        What matters most to your agency?
                      </Text>
                      <SimpleGrid
                        columns={{ base: 1, md: 3 }}
                        spacing={{ base: 3, md: 4 }}
                        maxW="900px"
                        mx="auto"
                      >
                        {CALC_TABS.map((tab) => {
                          const isActive = tab.id === active.id;
                          return (
                            <Box
                              key={tab.id}
                              as="button"
                              type="button"
                              onClick={() => setActiveCalc(tab.id)}
                              aria-pressed={isActive}
                              data-testid={`calc-tab-${tab.id}`}
                              textAlign="left"
                              bg={isActive ? 'white' : 'white'}
                              borderRadius="2xl"
                              border="2px solid"
                              borderColor={isActive ? `${tab.accent}.500` : 'gray.200'}
                              boxShadow={
                                isActive
                                  ? `0 10px 30px rgba(10,31,68,0.10)`
                                  : '0 1px 2px rgba(10,31,68,0.04)'
                              }
                              p={{ base: 4, md: 5 }}
                              transition="all 0.2s ease-out"
                              transform={isActive ? 'translateY(-2px)' : 'none'}
                              _hover={{
                                borderColor: isActive
                                  ? `${tab.accent}.500`
                                  : `${tab.accent}.300`,
                                boxShadow: '0 8px 20px rgba(10,31,68,0.08)',
                                transform: 'translateY(-2px)',
                              }}
                              _active={{ transform: 'translateY(0)' }}
                              position="relative"
                              overflow="hidden"
                            >
                              {isActive && (
                                <Box
                                  position="absolute"
                                  top={0}
                                  left={0}
                                  right={0}
                                  h="3px"
                                  bg={`${tab.accent}.500`}
                                />
                              )}
                              <HStack spacing={3} align="center">
                                <Flex
                                  w="44px"
                                  h="44px"
                                  borderRadius="xl"
                                  bg={isActive ? `${tab.accent}.500` : `${tab.accent}.50`}
                                  color={isActive ? 'white' : `${tab.accent}.600`}
                                  align="center"
                                  justify="center"
                                  flexShrink={0}
                                  transition="all 0.2s"
                                >
                                  <Icon as={tab.icon} boxSize={5} />
                                </Flex>
                                <VStack align="flex-start" spacing={0.5} flex="1">
                                  <Text
                                    fontWeight="bold"
                                    fontSize={{ base: 'sm', md: 'md' }}
                                    color={BRAND.ink}
                                    lineHeight={1.2}
                                  >
                                    {tab.title}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500" lineHeight={1.3}>
                                    {tab.subtitle}
                                  </Text>
                                </VStack>
                              </HStack>
                            </Box>
                          );
                        })}
                      </SimpleGrid>
                    </Box>

                    {/* The active calculator: single, generous, two-pane card */}
                    <AnimatedBox key={active.id} animation="fadeIn" delay={0.05} duration={0.35}>
                      <Box
                        bg="white"
                        borderRadius="3xl"
                        border="1px solid"
                        borderColor="gray.200"
                        boxShadow="0 4px 12px rgba(10,31,68,0.04), 0 24px 60px rgba(10,31,68,0.08)"
                        overflow="hidden"
                      >
                        <SimpleGrid
                          columns={{ base: 1, lg: 2 }}
                          spacing={0}
                          minH={{ lg: '520px' }}
                        >
                          {/* LEFT: inputs */}
                          <Box
                            p={{ base: 6, md: 10 }}
                            borderRight={{ base: 'none', lg: '1px solid' }}
                            borderBottom={{ base: '1px solid', lg: 'none' }}
                            borderColor="gray.100"
                          >
                            <VStack align="stretch" spacing={6}>
                              <HStack spacing={3}>
                                <Flex
                                  w="44px"
                                  h="44px"
                                  borderRadius="xl"
                                  bg={`${active.accent}.50`}
                                  color={`${active.accent}.600`}
                                  align="center"
                                  justify="center"
                                  flexShrink={0}
                                >
                                  <Icon as={active.icon} boxSize={5} />
                                </Flex>
                                <VStack align="start" spacing={0}>
                                  <Heading
                                    as="h3"
                                    fontSize={{ base: 'lg', md: 'xl' }}
                                    color={BRAND.ink}
                                    fontWeight="bold"
                                    letterSpacing="-0.01em"
                                  >
                                    {active.short}
                                  </Heading>
                                  <Text fontSize="sm" color="gray.500">
                                    {active.subtitle}
                                  </Text>
                                </VStack>
                              </HStack>

                              <VStack spacing={5} align="stretch">
                                {active.inputs.map(({ k, label, min, max, step, format }) => (
                                  <Box key={k}>
                                    <Flex justify="space-between" align="baseline" mb={2.5}>
                                      <Text
                                        fontSize="sm"
                                        fontWeight="semibold"
                                        color="gray.600"
                                      >
                                        {label}
                                      </Text>
                                      <Text
                                        fontSize="md"
                                        fontWeight="extrabold"
                                        color={`${active.accent}.600`}
                                      >
                                        {format(cv(k))}
                                      </Text>
                                    </Flex>
                                    <Slider
                                      aria-label={label}
                                      value={cv(k)}
                                      min={min}
                                      max={max}
                                      step={step}
                                      onChange={(val) => updateCalc(k, val)}
                                      colorScheme={active.accent}
                                      focusThumbOnChange={false}
                                    >
                                      <SliderTrack bg="gray.200" h="6px" borderRadius="full">
                                        <SliderFilledTrack />
                                      </SliderTrack>
                                      <SliderThumb
                                        boxSize={6}
                                        boxShadow="0 2px 8px rgba(10,31,68,0.15)"
                                        _focus={{
                                          boxShadow: `0 0 0 4px var(--chakra-colors-${active.accent}-100), 0 2px 8px rgba(10,31,68,0.15)`,
                                        }}
                                      />
                                    </Slider>
                                  </Box>
                                ))}
                              </VStack>
                            </VStack>
                          </Box>

                          {/* RIGHT: result + breakdown */}
                          <Box
                            position="relative"
                            p={{ base: 6, md: 10 }}
                            bg={`${active.accent}.50`}
                            overflow="hidden"
                          >
                            <Box
                              position="absolute"
                              top="-20%"
                              right="-15%"
                              w="320px"
                              h="320px"
                              borderRadius="full"
                              bg={`radial-gradient(circle, var(--chakra-colors-${active.accent}-200) 0%, transparent 70%)`}
                              opacity={0.55}
                              pointerEvents="none"
                            />
                            <Box
                              position="absolute"
                              bottom="-25%"
                              left="-10%"
                              w="280px"
                              h="280px"
                              borderRadius="full"
                              bg={`radial-gradient(circle, var(--chakra-colors-${active.accent}-100) 0%, transparent 70%)`}
                              opacity={0.6}
                              pointerEvents="none"
                            />

                            <VStack
                              align="stretch"
                              spacing={6}
                              h="full"
                              justify="space-between"
                              position="relative"
                              zIndex={1}
                            >
                              <VStack align="stretch" spacing={2}>
                                <Text
                                  fontSize="xs"
                                  fontWeight="bold"
                                  color={`${active.accent}.700`}
                                  letterSpacing="0.18em"
                                  textTransform="uppercase"
                                >
                                  Your projected impact
                                </Text>
                                <Heading
                                  as="div"
                                  fontSize={{ base: '5xl', md: '6xl', lg: '7xl' }}
                                  fontWeight="extrabold"
                                  color={`${active.accent}.700`}
                                  lineHeight={1}
                                  letterSpacing="-0.03em"
                                >
                                  {active.headlineValue}
                                </Heading>
                                <Text
                                  fontSize={{ base: 'md', md: 'lg' }}
                                  color={`${active.accent}.700`}
                                  fontWeight="semibold"
                                >
                                  {active.headlineLabel}
                                </Text>
                              </VStack>

                              <Box
                                bg="whiteAlpha.800"
                                borderRadius="xl"
                                p={{ base: 4, md: 5 }}
                                border="1px solid"
                                borderColor={`${active.accent}.100`}
                                backdropFilter="blur(8px)"
                              >
                                <Text
                                  fontSize="xs"
                                  fontWeight="bold"
                                  color="gray.500"
                                  letterSpacing="0.14em"
                                  textTransform="uppercase"
                                  mb={3}
                                >
                                  Breakdown
                                </Text>
                                <VStack spacing={2.5} align="stretch">
                                  {active.breakdown.map((row) => (
                                    <Flex
                                      key={row.label}
                                      justify="space-between"
                                      align="center"
                                    >
                                      <Text mb={0}
                                        fontSize={{ base: 'sm', md: 'md' }}
                                        color="gray.600"
                                      >
                                        {row.label}
                                      </Text>
                                      <Text mb={0}
                                        fontSize={{ base: 'md', md: 'lg' }}
                                        fontWeight="bold"
                                        color={
                                          row.highlight
                                            ? `${active.accent}.700`
                                            : BRAND.ink
                                        }
                                      >
                                        {row.value}
                                      </Text>
                                    </Flex>
                                  ))}
                                </VStack>
                              </Box>

                              <Button
                                onClick={scrollToSignup}
                                colorScheme={active.accent}
                                size="lg"
                                rightIcon={<FaArrowRight />}
                                borderRadius="lg"
                                fontWeight="semibold"
                                boxShadow={`0 8px 20px var(--chakra-colors-${active.accent}-200)`}
                                _hover={{
                                  transform: 'translateY(-1px)',
                                  boxShadow: `0 12px 28px var(--chakra-colors-${active.accent}-300)`,
                                }}
                                _active={{ transform: 'scale(0.99)' }}
                                transition="all 0.2s"
                              >
                                Capture this revenue
                              </Button>
                            </VStack>
                          </Box>
                        </SimpleGrid>
                      </Box>
                    </AnimatedBox>
                  </VStack>
                );
              })()}

              <Flex justify="center" pt={2}>
                <HStack spacing={2} color="gray.500" fontSize="sm">
                  <Icon as={FaCalculator} boxSize={3.5} />
                  <Text mb={0}>Estimates based on your inputs. Actual results will vary.</Text>
                </HStack>
              </Flex>
            </VStack>
          </Container>
        </Box>

        {/* ============================================================ */}
        {/* PRODUCT DEMO VIDEO                                          */}
        {/* ============================================================ */}
        <Box py={SECTION_PADDING} bg="gray.900" id="agency-demo-video">
          <Container maxW="6xl">
            <VStack spacing={8}>
              <VStack spacing={3} textAlign="center" maxW="900px">
                <AnimatedBox animation="fadeIn" delay={0.1}>
                  <Badge
                    bg="blue.500"
                    color="white"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="xs"
                    fontWeight="semibold"
                    letterSpacing="wider"
                    textTransform="uppercase"
                  >
                    Watch the platform
                  </Badge>
                </AnimatedBox>
                <AnimatedBox animation="slideUp" delay={0.2}>
                  <Heading
                    as="h2"
                    fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                    color="white"
                    fontWeight="extrabold"
                    lineHeight={1.15}
                    letterSpacing="-0.02em"
                  >
                    See what your clients will see.
                  </Heading>
                </AnimatedBox>
                <AnimatedBox animation="fadeIn" delay={0.3}>
                  <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.400" maxW="640px">
                    A 2-minute walkthrough of the actual product — reviews, traffic, SEO, ads, AI
                    insights and the playbook. Same product, your branding.
                  </Text>
                </AnimatedBox>
              </VStack>

              <AnimatedBox animation="scale" delay={0.4} w="full" maxW="960px">
                <Box
                  borderRadius="2xl"
                  overflow="hidden"
                  boxShadow="0 20px 60px rgba(0,0,0,0.45)"
                  position="relative"
                  bg="gray.800"
                >
                  <AspectRatio ratio={16 / 9}>
                    <iframe
                      id="agencyDemoVideo"
                      title="Localytics Agency Platform Demo"
                      src="https://www.youtube.com/embed/svKEXlEmb_8?rel=0&modestbranding=1"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      style={{ border: 0, width: '100%', height: '100%' }}
                    />
                  </AspectRatio>
                </Box>
              </AnimatedBox>
            </VStack>
          </Container>
        </Box>

        {/* ============================================================ */}
        {/* HOW IT WORKS                                                */}
        {/* ============================================================ */}
        <Box py={SECTION_PADDING} bg="gray.50">
          <Container maxW={CONTAINER_MAX}>
            <VStack spacing={12} align="stretch">
              <SectionHeader
                eyebrow="How it works"
                title="Three steps to"
                accent="blowing their mind"
                subtitle="No APIs. No Google Analytics. No plugins. Just tell us the niche and location."
              />

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                {HOW_IT_WORKS_STEPS.map((item, index) => (
                  <AnimatedBox key={item.step} animation="slideUp" delay={0.1 + index * 0.1}>
                    <Card>
                      <VStack align="flex-start" spacing={4}>
                        <Text
                          fontSize="4xl"
                          fontWeight="extrabold"
                          color={BRAND.primary}
                          lineHeight={1}
                          letterSpacing="-0.02em"
                        >
                          {item.step}
                        </Text>
                        <Heading as="h3" size="md" color={BRAND.ink} fontWeight="bold">
                          {item.title}
                        </Heading>
                        <Text color="gray.600" fontSize="md" lineHeight={1.65}>
                          {item.description}
                        </Text>
                      </VStack>
                    </Card>
                  </AnimatedBox>
                ))}
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

        {/* ============================================================ */}
        {/* WHY IT WORKS — 4 PILLARS                                   */}
        {/* ============================================================ */}
        <Box py={SECTION_PADDING} bg="gray.50">
          <Container maxW={CONTAINER_MAX}>
            <VStack spacing={12} align="stretch">
              <SectionHeader
                eyebrow="Why it works"
                title="Four ways blowing their mind"
                accent="grows your agency"
                subtitle="When prospects see data on their own market that they've never laid eyes on, the conversation changes completely."
              />

              <AnimatedBox animation="slideUp" delay={0.1}>
                <FourPillarsSpotlight onCta={scrollToSignup} />
              </AnimatedBox>
            </VStack>
          </Container>
        </Box>

        {/* ============================================================ */}
        {/* DISCOVERY CALL EXAMPLES                                     */}
        {/* ============================================================ */}
        <Box py={SECTION_PADDING} bg="white" position="relative" overflow="hidden">
          {/* Faint dotted grid behind the discovery-call section so the
              white-on-white cards have a softer technical texture. */}
          <Box
            position="absolute"
            inset={0}
            bgImage="radial-gradient(circle, #cbd5e1 1px, transparent 1px)"
            bgSize="24px 24px"
            opacity={0.3}
            pointerEvents="none"
          />
          <Container maxW={CONTAINER_MAX} position="relative" zIndex={1}>
            <VStack spacing={12} align="stretch">
              <SectionHeader
                eyebrow="The discovery call advantage"
                title="Walk in knowing everything."
                accent="They walk out impressed."
                subtitle="Pull up any prospect's market before the call. See exactly where they rank, who's beating them, and what they need."
              />

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <Card position="relative" overflow="hidden">
                  <Text
                    position="absolute"
                    top={-4}
                    left={2}
                    fontSize="8xl"
                    color="gray.200"
                    fontFamily="Georgia, serif"
                    lineHeight={1}
                    pointerEvents="none"
                    aria-hidden
                  >
                    &ldquo;
                  </Text>
                  <VStack align="flex-start" spacing={4} position="relative" zIndex={1}>
                    <HStack spacing={3}>
                      <IconTile icon={FaLightbulb} accent="green" />
                      <Badge
                        bg="green.50"
                        color="green.700"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontSize="xs"
                        fontWeight="semibold"
                      >
                        Example · Strong Performer
                      </Badge>
                    </HStack>
                    <Text color="#334155" fontSize="lg" lineHeight={1.7} fontStyle="italic">
                      You guys are doing an amazing job at review growth and SEO score. But
                      we need to work on sentiment — that&apos;s a big issue for your business.
                      Here&apos;s what customers are actually saying in your 1-star reviews…
                    </Text>
                    <Divider />
                    <HStack spacing={2} flexWrap="wrap">
                      <Badge
                        colorScheme="green"
                        fontSize="xs"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontFamily="mono"
                      >
                        2nd · review growth
                      </Badge>
                      <Badge
                        colorScheme="green"
                        fontSize="xs"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontFamily="mono"
                      >
                        3rd · SEO
                      </Badge>
                      <Badge
                        colorScheme="red"
                        fontSize="xs"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontFamily="mono"
                      >
                        15th · sentiment
                      </Badge>
                    </HStack>
                  </VStack>
                </Card>

                <Card position="relative" overflow="hidden">
                  <Text
                    position="absolute"
                    top={-4}
                    left={2}
                    fontSize="8xl"
                    color="gray.200"
                    fontFamily="Georgia, serif"
                    lineHeight={1}
                    pointerEvents="none"
                    aria-hidden
                  >
                    &ldquo;
                  </Text>
                  <VStack align="flex-start" spacing={4} position="relative" zIndex={1}>
                    <HStack spacing={3}>
                      <IconTile icon={FaArrowUp} accent="blue" />
                      <Badge
                        bg="blue.50"
                        color="blue.700"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontSize="xs"
                        fontWeight="semibold"
                      >
                        Example · Growth Opportunity
                      </Badge>
                    </HStack>
                    <Text color="#334155" fontSize="lg" lineHeight={1.7} fontStyle="italic">
                      You&apos;re 12th in SEO, 6th in traffic, 6th in review growth.
                      We&apos;re going to get you more backlinks, which is going to boost your
                      website traffic, and we&apos;re going to implement a new review garnering
                      system…
                    </Text>
                    <Divider />
                    <HStack spacing={2} flexWrap="wrap">
                      <Badge
                        colorScheme="orange"
                        fontSize="xs"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontFamily="mono"
                      >
                        12th · SEO
                      </Badge>
                      <Badge
                        colorScheme="yellow"
                        fontSize="xs"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontFamily="mono"
                      >
                        6th · traffic
                      </Badge>
                      <Badge
                        colorScheme="yellow"
                        fontSize="xs"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontFamily="mono"
                      >
                        6th · review growth
                      </Badge>
                    </HStack>
                  </VStack>
                </Card>
              </SimpleGrid>

              <Box
                bg="blue.50"
                border="1px solid"
                borderColor="blue.100"
                borderRadius="xl"
                p={6}
                maxW="820px"
                mx="auto"
                textAlign="center"
              >
                <HStack spacing={3} justify="center">
                  <Icon as={FaLightbulb} color={BRAND.primary} boxSize={5} flexShrink={0} />
                  <Text mb={0} fontSize="md" color={BRAND.ink} lineHeight={1.6}>
                    Click on any business and see where they rank for each KPI — so you always walk in with full context.
                  </Text>
                </HStack>
              </Box>
            </VStack>
          </Container>
        </Box>

        {/* ============================================================ */}
        {/* FAQ                                                         */}
        {/* ============================================================ */}
        <Box py={SECTION_PADDING} bg="white" id="faq">
          <Container maxW="3xl">
            <VStack spacing={12} align="stretch">
              <SectionHeader eyebrow="FAQ" title="Common questions" />

              {/* Single bordered container so FAQ rows feel like one cohesive
                  panel rather than floating cards. Each row has a hover wash
                  and the chevron rotates rather than swapping icons. */}
              <Box
                bg="white"
                borderRadius="2xl"
                border="1px solid"
                borderColor="gray.200"
                overflow="hidden"
                boxShadow="0 1px 2px rgba(10,31,68,0.04)"
              >
                {AGENCY_FAQ.map((faq, index) => {
                  const isOpen = openFaqIndex === index;
                  const isLast = index === AGENCY_FAQ.length - 1;
                  return (
                    <Box
                      key={faq.question}
                      borderBottom={isLast ? 'none' : '1px solid'}
                      borderColor="gray.100"
                      transition="background 0.2s"
                      _hover={{ bg: 'gray.50' }}
                    >
                      <Button
                        w="full"
                        h="auto"
                        py={5}
                        px={6}
                        bg="transparent"
                        _hover={{ bg: 'transparent' }}
                        _active={{ bg: 'gray.100' }}
                        onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                        borderRadius="none"
                        textAlign="left"
                        fontWeight="normal"
                      >
                        <HStack w="full" justify="space-between" align="center" spacing={4}>
                          <Text mb={0}
                            fontWeight="semibold"
                            color={BRAND.ink}
                            fontSize={{ base: 'sm', md: 'md' }}
                            lineHeight={1.5}
                            flex="1"
                            whiteSpace="normal"
                          >
                            {faq.question}
                          </Text>
                          <Flex
                            w="28px"
                            h="28px"
                            bg={isOpen ? 'blue.50' : 'gray.100'}
                            borderRadius="full"
                            align="center"
                            justify="center"
                            flexShrink={0}
                            transition="background 0.2s"
                          >
                            <Icon
                              as={FaChevronDown}
                              color={isOpen ? 'blue.600' : 'gray.600'}
                              boxSize={3}
                              transform={isOpen ? 'rotate(180deg)' : 'rotate(0)'}
                              transition="transform 0.2s ease-out"
                            />
                          </Flex>
                        </HStack>
                      </Button>
                      <Box
                        maxH={isOpen ? '600px' : '0'}
                        overflow="hidden"
                        transition="max-height 0.3s ease-in-out"
                        borderTop={isOpen ? '1px solid' : 'none'}
                        borderColor="gray.100"
                      >
                        <Box px={6} pt={4} pb={5}>
                          <Text
                            color="#475569"
                            fontSize={{ base: 'sm', md: 'md' }}
                            lineHeight={1.7}
                          >
                            {faq.answer}
                          </Text>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </VStack>
          </Container>
        </Box>

        {/* ============================================================ */}
        {/* FINAL CTA                                                   */}
        {/* ============================================================ */}
        <Box py={{ base: 20, md: 28, lg: 32 }} bg={BRAND.heroBg} position="relative" overflow="hidden">
          {/* Layered glow mesh: a wide blue halo + a smaller sky pulse so
              the closing section feels alive instead of empty. */}
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            w="900px"
            h="900px"
            borderRadius="full"
            bg="radial-gradient(circle, rgba(29,110,245,0.20) 0%, transparent 60%)"
            pointerEvents="none"
          />
          <Box
            position="absolute"
            top="20%"
            left="20%"
            w="400px"
            h="400px"
            borderRadius="full"
            bg="radial-gradient(circle, rgba(59,166,245,0.18) 0%, transparent 70%)"
            pointerEvents="none"
          />
          <Box
            position="absolute"
            bottom="10%"
            right="15%"
            w="500px"
            h="500px"
            borderRadius="full"
            bg="radial-gradient(circle, rgba(124,196,255,0.10) 0%, transparent 70%)"
            pointerEvents="none"
          />
          <Container maxW="3xl" position="relative" zIndex={1}>
            <VStack spacing={6} textAlign="center">
              <Heading
                as="h2"
                fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                color="white"
                lineHeight={1.1}
                fontWeight="extrabold"
                letterSpacing="-0.025em"
              >
                Ready to <GradientText>blow their mind</GradientText>?
              </Heading>
              <Text fontSize={{ base: 'lg', md: 'xl' }} color="whiteAlpha.800" maxW="560px" lineHeight={1.6}>
                All public data. No APIs, Google Analytics, or plugins required. Instant setup.
                Completely whitelabeled under your brand.
              </Text>
              <Flex
                direction={{ base: 'column', sm: 'row' }}
                gap={4}
                justify="center"
                w={{ base: 'full', sm: 'auto' }}
                pt={4}
              >
                <Button
                  onClick={scrollToSignup}
                  bg="white"
                  color={BRAND.deep}
                  fontWeight="semibold"
                  px={10}
                  py={6}
                  size="lg"
                  borderRadius="lg"
                  rightIcon={<FaArrowRight />}
                  _hover={{
                    bg: 'whiteAlpha.900',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 10px 24px rgba(255,255,255,0.18)',
                    textDecoration: 'none',
                  }}
                  _active={{ transform: 'scale(0.98)' }}
                  transition="all 0.2s"
                >
                  Get Started
                </Button>
                <Button
                  as="a"
                  href="https://calendly.com/walterlocalytics/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  leftIcon={<Icon as={FaCalendarAlt} />}
                  bg="transparent"
                  color="white"
                  borderWidth="1px"
                  borderColor="whiteAlpha.300"
                  fontWeight="semibold"
                  px={10}
                  py={6}
                  size="lg"
                  borderRadius="lg"
                  _hover={{
                    bg: 'whiteAlpha.100',
                    borderColor: 'whiteAlpha.500',
                    transform: 'translateY(-1px)',
                    textDecoration: 'none',
                  }}
                  _active={{ transform: 'scale(0.98)' }}
                  transition="all 0.2s"
                >
                  Book a Call
                </Button>
              </Flex>
              <HStack spacing={6} pt={4} flexWrap="wrap" justify="center">
                {[
                  '$500/mo for 20 market reports',
                  '$90/client/mo for access',
                  'No long-term contracts',
                ].map((item) => (
                  <HStack key={item} spacing={2}>
                    <Icon as={FaCheckCircle} color="#7cb8ff" boxSize={4} />
                    <Text mb={0} color="whiteAlpha.800" fontSize="sm">
                      {item}
                    </Text>
                  </HStack>
                ))}
              </HStack>
            </VStack>
          </Container>
        </Box>

        {/* ============================================================ */}
        {/* FOOTER                                                      */}
        {/* ============================================================ */}
        <Box py={8} bg="#080814">
          <Container maxW={CONTAINER_MAX}>
            <Flex
              direction={{ base: 'column', md: 'row' }}
              justify="space-between"
              align="center"
              gap={4}
            >
              <Box as={RouterLink} to="/landing">
                <Image
                  src="/output.png"
                  alt="Localytics"
                  h="32px"
                  w="auto"
                  objectFit="contain"
                  filter="brightness(0) invert(0.7)"
                />
              </Box>
              <HStack spacing={4} flexWrap="wrap" justify="center">
                <Button
                  variant="link"
                  color="gray.500"
                  fontSize="sm"
                  fontWeight="medium"
                  _hover={{ color: 'white', textDecoration: 'none' }}
                  as={RouterLink}
                  to="/landing"
                >
                  For Businesses
                </Button>
                <Button
                  variant="link"
                  color="gray.500"
                  fontSize="sm"
                  fontWeight="medium"
                  _hover={{ color: 'white', textDecoration: 'none' }}
                  as="a"
                  href="https://www.instagram.com/reel/DUW8yvxDJtz/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Instagram
                </Button>
                <Button
                  variant="link"
                  color="gray.500"
                  fontSize="sm"
                  fontWeight="medium"
                  _hover={{ color: 'white', textDecoration: 'none' }}
                  as="a"
                  href="https://www.linkedin.com/company/localytics-ai/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  LinkedIn
                </Button>
                <Button
                  variant="link"
                  color="gray.500"
                  fontSize="sm"
                  fontWeight="medium"
                  _hover={{ color: 'white', textDecoration: 'none' }}
                  as={RouterLink}
                  to="/login"
                >
                  Login
                </Button>
              </HStack>
              <VStack spacing={0.5} align={{ base: 'center', md: 'flex-end' }}>
                <Text color="gray.600" fontSize="xs">
                  &copy; {new Date().getFullYear()} Localytics. All rights reserved.
                </Text>
                <Text color="gray.700" fontSize="xs">
                  Started, owned &amp; operated in Nashville, TN.
                </Text>
              </VStack>
            </Flex>
          </Container>
        </Box>
      </Box>
      {!isHeroExpanded && <AgencyFloatingProspectBar />}
    </>
  );
};

export default AgencyLanding;
